import { DateTime } from 'luxon';
import { promisify } from 'util'
import zlib from 'zlib'

import { apiCall, secondaryApi } from "./ApiUtils";
import { capitalize, combineObjects } from './common';

const gunzip = promisify(zlib.gunzip)

export const getDailyLeadsBySite = (siteId, startDate, endDate, dtCreated=false) => apiCall(`leads/${siteId}/${startDate}/${endDate}${dtCreated ? "/created" : ""}`, {
    method: "GET"
}, null, secondaryApi)

export const getMonthlyLeadsLongTerm = (siteId, lastYear, currentYear, format="year", signal, altToken=null, query=null) => apiCall(`leads/month/${siteId}/${lastYear}/${currentYear}?format=${format}${altToken ? '&altToken=1': ''}${query ? '&'+ Object.keys(query).map(k => `${k}=${query[k]}`).join("&") : '' }`, {
    method: 'GET'
}, null, secondaryApi, signal, altToken)

export const getLeadSiteMap = (siteId, includeSecrets=false, altToken=null) => apiCall(`admin/lead-sitemap/${siteId}?${includeSecrets ? "includeSecrets=1" : ""}${altToken ? "&altToken=1" : ""}`, {
    method: "GET"
}, null, secondaryApi, null, altToken)

export const createLeadSiteMap = (siteId) => apiCall(`admin/lead-sitemap/${siteId}`, {
    method: "POST"
}, null, secondaryApi)

export const updateLeadSiteMap = (body) => apiCall(`admin/lead-sitemap/${body.siteRefId}`, {
    method: "PUT",
    body
}, null, secondaryApi)

export const processMonthlyLeads = async(metrics) => {
    let leadBinary
    if(metrics?.leads){
        leadBinary = await gunzip(Buffer.from(metrics?.leads ?? ""))
    }
    return {
        ...metrics,
        leads: metrics?.leads ? JSON.parse(leadBinary) : []
    }
}

export const BROKER_SITES = [
    "FranServe",
    "IFPG",
    "TES",
    "FranChoice",
    "FBA",
    "FranNet",
    "FCC",
    "Franchise City",
    "Pinnacle",
    "Vetrepreneur",
    "Franchise Fit",
    "The Perfect Franchise",
    "Other Source"
]

export const INTEGRATION_TYPES = {
    activecampaign: "Active Campaign",
    franconnect: "FranConnect",
    caringsenior: "Caring Senior Service",
    gohighlevel: "Go HighLevel",
    clienttether: "Client Tether",
    franchisesoft: "Franchise Soft",
    repm: "Repm Group",
    hubspot: "HubSpot"
}

export const LEAD_SUMMARY_TPL = {
    "sources": {
        "(N/A)": {
            "totalContacted": 1,
            "revenue": 1,
            "mediums": {
                "(N/A)": {
                    "revenue": 1,
                    "unitSales": 1
                }
            },
            "unitSales": 1,
            "totalEngaged": 1,
            "totalFinanciallyQualifiedLeads": 1,
            "totalAIEngaged": 1
        }
    },
    "totalLeadToAppDays": 1,
    "totalLeadToDeal": 1,
    "totalCancelled": 1,
    "totalEngaged": 1,
    "totalBookings": 5,
    "totalNoShow": 1,
    "totalLeadToApp": 1,
    "totalLeadToDealDays": 1,
    "personas": {
        "FranDevPro": {
            "totalDiscovered": 1,
            "totalCancelled": 1,
            "totalEngaged": 1,
            "totalContacted": 1,
            "totalNoShow": 1,
            "totalBookingsKept": 1,
            "totalRescheduled": 1,
            "totalFinanciallyQualifiedLeads": 1,
            "totalAIEngaged": 1
        },
        "Main": {
            "totalDiscovered": 1,
            "totalCancelled": 1,
            "totalEngaged": 1,
            "totalContacted": 1,
            "totalNoShow": 1,
            "totalBookingsKept": 1,
            "totalRescheduled": 1,
            "totalFinanciallyQualifiedLeads": 1,
            "totalAIEngaged": 1
        }
    },
    "totalRescheduled": 1,
    "unitSales": 1,
    "totalDiscovered": 1,
    "revenue": 1,
    "totalLeads": 1,
    "dailyBookings": {
        "2024-01-01": {
            x: "2024-01-01",
            y: 1
        }
    },
    "totalAppsSigned": 1,
    "totalInclusiveBookings": 1,
    "totalContacted": 1,
    "totalBookingsKept": 1,
    "totalFinanciallyQualifiedLeads": 1,
    "totalAIEngaged": 1,
    "dealTypes": {
        "(N/A)": {
            "totalDiscovered": 1,
            "revenue": 1,
            "totalAppsSigned": 1,
            "totalCancelled": 1,
            "totalEngaged": 1,
            "totalContacted": 1,
            "totalNoShow": 1,
            "totalRescheduled": 1,
            "unitSales": 1
        }
    }
}

export const FIELD_ASSOCIATIONS = {
    "leadId": "Lead ID",
    "cancelledTimestamp": "Date Canceled",
    "timestamp": "Date Created",
    "closeTimestamp": "Date Close Override",
    "engagementTimestamp": "Date Contact Engages",
    "contactedTimestamp": "Date Contacted/Booked",
    "noShowTimestamp": "Date No Show",
    "rescheduledTimestamp": "Date Rescheduled",
    "signedTimestamp": "Date App Signed",
    "utm_campaign": "UTM Campaign",
    "utm_medium": "UTM Medium",
    "utm_source": "UTM Source",
    "alt_utm_campaign": "Alternate UTM Campaign",
    "alt_utm_medium": "Alternate UTM Medium",
    "alt_utm_source": "Alternate UTM Source",
    "persona": "Persona",
    "discoveryDayTimestamp": "Discovery Day",
    "bookingKept": "Booking Kept",
    "dealType": "Deal Type",
    "revenue": "Revenue",
    "veteran": "Veteran",
    "isContacted": "Has Been Contacted",
    "status": "Lead Status",
    "minority": "DEI/Veteran",
    "zip": "Zip Code",
    "territoryStatus": "Territory Status",
    "adjustedTerritory": "Adjusted Territory",
    "financiallyQualified": "Financially Qualified",
    "aiInitEngagementTimestamp": "Date AI Engaged Contact",
    "smsIneligible": "SMS Ineligible",
    "dqReason": "DQ Reason",
    "deferredDate": "Deferred Date",
    "unsubscribed": "Contact Unsubscribed",
    "optedOut": "Opt Out",
    "bookingCreatedAtTimestamp": "Date Booking Created At",
    "unitsSoldOverride": "# Of Units Sold Override",
    "appSignedFinanciallyQualified": "Financially Qualified for App Signed",
    "fddSignedTimestamp": "Date FDD Signed",
    "bookingType": "Booking Type",
    "leadOwnerName": "Lead Owner Name",
    "brokerCommission": "Broker Commission"
}

export const FIELD_CATEGORIES = {
    "utm_campaign": "Lead Source Data",
    "utm_medium": "Lead Source Data",
    "utm_source": "Lead Source Data",
    "alt_utm_campaign": "Lead Source Data",
    "alt_utm_medium": "Lead Source Data",
    "alt_utm_source": "Lead Source Data",
    "persona": "Lead Source Data",
    "dealType": "Lead Source Data",
    "veteran": "Lead Source Data",
    "minority": "Lead Source Data",
    "cancelledTimestamp": "Booking Stats + Funnel Stages",
    "timestamp": "Booking Stats + Funnel Stages",
    "closeTimestamp": "Booking Stats + Funnel Stages",
    "engagementTimestamp": "Booking Stats + Funnel Stages",
    "contactedTimestamp": "Booking Stats + Funnel Stages",
    "noShowTimestamp": "Booking Stats + Funnel Stages",
    "rescheduledTimestamp": "Booking Stats + Funnel Stages",
    "signedTimestamp": "Booking Stats + Funnel Stages",
    "appSignedFinanciallyQualified": "Booking Stats + Funnel Stages",
    "discoveryDayTimestamp": "Booking Stats + Funnel Stages",
    "bookingKept": "Booking Stats + Funnel Stages",
    "isContacted": "Booking Stats + Funnel Stages",
    "aiInitEngagementTimestamp": "Booking Stats + Funnel Stages",
    "leadOwnerName": "General Details",
    "revenue": "General Details",
    "status": "General Details",
    "zip": "General Details",
    "dqReason": "General Details",
    "deferredDate": "General Details",
    "brokerCommission": "General Details",
    "leadId": "Lead Source Data",
    "territoryStatus": "Territory Checker",
    "adjustedTerritory": "Territory Checker",
    "financiallyQualified": "General Details",
    "smsIneligible": "Booking Stats + Funnel Stages",
    "optedOut": "Booking Stats + Funnel Stages",
    "unsubscribed": "Booking Stats + Funnel Stages",
    "bookingCreatedAtTimestamp": "Booking Stats + Funnel Stages",
    "unitsSoldOverride": "Lead Source Data",
    "fddSignedTimestamp": "Booking Stats + Funnel Stages",
    "bookingType": "Booking Stats + Funnel Stages"
}

export const FIELD_CATEGORIY_TITLES = [
    "General Details",
    "Lead Source Data",
    "Booking Stats + Funnel Stages",
    "Territory Checker"
]

export const processDailyLeads = (metrics, startDate=DateTime.local().minus({months: 1, days: 1}).toISODate(), endDate=DateTime.local().minus({days: 1}).toISODate()) => {
    const sDate = DateTime.fromISO(startDate)
    const eDate = DateTime.fromISO(endDate)

    const sDateMillis = sDate.startOf("day").toMillis()
    const eDateMillis = eDate.endOf("day").toMillis()

    const dailyLeads = {}
    const dailyBookings = {}
    const dailyBookingsKept = {}

    const dailyFinanciallyQualified = {}
    const dailyAppsSigned = {}
    const dailyFDDSigned = {}

    let tempDate = sDate

    while(tempDate <= eDate){
        dailyLeads[tempDate.toISODate()] = {
            x: tempDate.toFormat("LL-dd"),
            y: 0    
        }
        dailyBookings[tempDate.toISODate()] = {
            x: tempDate.toFormat("LL-dd"),
            y: 0    
        }
        dailyBookingsKept[tempDate.toISODate()] = {
            x: tempDate.toFormat("LL-dd"),
            y: 0
        }
        dailyFinanciallyQualified[tempDate.toISODate()] = {
            x: tempDate.toFormat("LL-dd"),
            y: 0
        }
        dailyAppsSigned[tempDate.toISODate()] = {
            x: tempDate.toFormat("LL-dd"),
            y: 0
        }
        dailyFDDSigned[tempDate.toISODate()] = {
            x: tempDate.toFormat("LL-dd"),
            y: 0
        }

        tempDate = tempDate.plus({days: 1})
    }

    const metricBySummary = metrics.reduce((prev, curr, idx, arr) => {
        let source = (curr.metrics?.utm_source && curr.metrics?.utm_source !== "" ? curr.metrics?.utm_source.toString() : "(N/A)").trim()
        let medium = (curr.metrics?.utm_medium && curr.metrics?.utm_medium !== "" ? curr.metrics?.utm_medium.toString() : "(N/A)").trim()
        let campaign = (curr.metrics?.utm_campaign && curr.metrics?.utm_campaign !== "" ? curr.metrics?.utm_campaign.toString() : "(N/A)").trim()
        
        if((!source || source === "(N/A)") && 
            curr?.metrics?.alt_utm_source && 
            curr?.metrics?.alt_utm_source !== ""){
            source = curr?.metrics?.alt_utm_source
        }
        if((!medium || medium === "(N/A)") && 
            curr?.metrics?.alt_utm_medium && 
            curr?.metrics?.alt_utm_medium !== ""){
            medium = curr?.metrics?.alt_utm_medium
        }
        if((!campaign || campaign === "(N/A)") && 
            curr?.metrics?.alt_utm_campaign && 
            curr?.metrics?.alt_utm_campaign !== ""){
            campaign = curr?.metrics?.alt_utm_campaign
        }

        const persona = (curr.metrics?.persona && curr.metrics?.persona !== "" ? curr.metrics?.persona : "Main").trim()

        const financiallyQualified = curr?.metrics?.financiallyQualified ?? false
        const appSignedFinanciallyQualified = curr?.metrics?.appSignedFinanciallyQualified ?? false

        const territoryStatus = curr?.metrics?.territoryStatus ? curr.metrics?.territoryStatus : ""
        const adjustedTerritory = curr?.metrics?.adjustedTerritory ? curr?.metrics?.adjustedTerritory : null

        const dealCloseOverride = curr?.metrics?.dealCloseOverride ? curr.metrics?.dealCloseOverride : false

        const bookingType = curr?.bookingType ? curr?.bookingType : "SMS"

        const dealType = curr?.dealType && curr?.dealType !== "" ? curr?.dealType : "(N/A)"

        const dqReason = (curr?.metrics?.dqReason && curr.metrics?.dqReason !== "" ? curr.metrics?.dqReason : null)

        const leadOwnerName = (curr?.metrics?.leadOwnerName && curr?.metrics?.leadOwnerName !== "" ? curr.metrics?.leadOwnerName : "N/A")

        const numberOfUnitsSold = curr?.metrics?.unitsSoldOverride && curr?.metrics?.unitsSoldOverride !== null ? parseInt(curr?.metrics?.unitsSoldOverride) : 1 

        if(prev.leadOwners[leadOwnerName] === undefined){
            prev.leadOwners[leadOwnerName] = {
                totalLeads: 0,
                totalContacted: 0,
                totalContactedAllTypes: 0,
                totalRescheduled: 0,
                totalBookingsKept: 0,
                totalBookingsKeptAllTypes: 0,
                totalNoShow: 0,
                totalCancelled: 0,
                unitSales: 0,
                revenue: 0,
                totalUnsubscribed: 0,
                totalOptedOut: 0,
                brokerCommission: 0
            }
        }

        if(prev.sources[source] === undefined){
            prev.sources[source] = {
                revenue: 0,
                unitSales: 0,
                mediums: {},
                campaigns: {},
                totalContacted: 0,
                totalContactedAllTypes: 0,
                totalEngaged: 0,
                totalFinanciallyQualifiedLeads: 0,
                totalFinanciallyQualifiedAppSigned: 0,
                totalAIEngaged: 0,
                totalLeads: 0,
                totalUnsubscribed: 0,
                totalOptedOut: 0,
                brokerCommission: 0
            }
        }

        if(prev.personas[persona] === undefined){
            prev.personas[persona] = {
                totalEngaged: 0,
                totalContacted: 0,
                totalContactedAllTypes: 0,
                totalBookingsAllTypes: 0,
                totalRescheduled: 0,
                totalDiscovered: 0,
                totalBookingsKept: 0,
                totalBookingsKeptAllTypes: 0,
                totalNoShow: 0,
                totalOptedOut: 0,
                totalCancelled: 0,
                totalAIEngaged: 0,
                totalAppsSigned: 0,
                totalLeads: 0,
                totalUnsubscribed: 0
            }
        }

        if(dqReason && prev.dqReasons[dqReason] === undefined){
            prev.dqReasons[dqReason] = {
                sources: {},
                dealValue: 0,
                totalLeads: 0
            }
        }

        if(dqReason && prev.dqReasons[dqReason].sources[source] === undefined){
            prev.dqReasons[dqReason].sources[source] = {
                mediums: {},
                dealValue: 0,
                totalLeads: 0
            }
        }

        if(dqReason && prev.dqReasons[dqReason].sources[source].mediums[medium] === undefined){
            prev.dqReasons[dqReason].sources[source].mediums[medium] = {
                dealValue: 0,
                totalLeads: 0
            }
        }

        if(prev.dealTypes[dealType] === undefined){
            prev.dealTypes[dealType] = {
                revenue: 0,
                unitSales: 0,
                totalContacted: 0,
                totalContactedAllTypes: 0,
                totalAppsSigned: 0,
                totalFDDSigned: 0,
                totalCancelled: 0,
                totalNoShow: 0,
                totalRescheduled: 0,
                totalDiscovered: 0,
                totalEngaged: 0,
                brokerCommission: 0
            }
        }

        if(curr.status.toLowerCase() === "won" &&
            // !dealCloseOverride &&
            curr.closeTimestamp >= sDateMillis &&
            curr.closeTimestamp <= eDateMillis){
            prev.sources[source].revenue += curr?.revenue ?? 0         
            prev.sources[source].unitSales += numberOfUnitsSold

            prev.leadOwners[leadOwnerName].revenue += curr?.revenue ?? 0         
            prev.leadOwners[leadOwnerName].unitSales += numberOfUnitsSold
        }

        if(prev.sources[source].mediums[medium] === undefined){
            prev.sources[source].mediums[medium] = {
                campaigns: {},
                revenue: 0,
                unitSales: 0,
                totalContacted: 0,
                totalContactedAllTypes: 0,
                totalAppsSigned: 0,
                totalFDDSigned: 0,
                totalDiscovered: 0,
                totalEngaged: 0,
                totalCancelled: 0,
                totalNoShow: 0,
                totalBookingsKept: 0,
                totalBookingsKeptAllTypes: 0,
                totalRescheduled: 0,
                totalFinanciallyQualifiedLeads: 0,
                totalFinanciallyQualifiedAppSigned: 0,
                totalDqReasonLeads: 0,
                totalLeads: 0,
                brokerCommission: 0
            }
        }

        if(prev.sources[source].campaigns[campaign] === undefined){
            prev.sources[source].campaigns[campaign] = {
                totalContacted: 0,
                totalContactedAllTypes: 0,
                totalAppsSigned: 0,
                totalFDDSigned: 0,
                totalDiscovered: 0,
                totalEngaged: 0,
                totalCancelled: 0,
                totalNoShow: 0,
                totalBookingsKept: 0,
                totalBookingsKeptAllTypes: 0,
                totalRescheduled: 0,
                totalLeads: 0,
                totalFinanciallyQualifiedLeads: 0,
                totalFinanciallyQualifiedAppSigned: 0,
                totalUnsubscribed: 0,
                totalOptedOut: 0,
                totalOpenTerritories: 0,
                totalClosedTerritories: 0,
                totalPendingTerritories: 0,
                totalAdjustedTerritories: 0,
                totalUnknownTerritories: 0,
                totalAppsSignedBooked: 0
            }
        }

        if(prev.sources[source].mediums[medium].campaigns[campaign] === undefined){
            prev.sources[source].mediums[medium].campaigns[campaign] = {
                totalContacted: 0,
                totalContactedAllTypes: 0,
                totalAppsSigned: 0,
                totalFDDSigned: 0,
                totalDiscovered: 0,
                totalEngaged: 0,
                totalCancelled: 0,
                totalNoShow: 0,
                totalBookingsKept: 0,
                totalBookingsKeptAllTypes: 0,
                totalRescheduled: 0,
                totalFinanciallyQualifiedLeads: 0,
                totalFinanciallyQualifiedAppSigned: 0,
                totalLeads: 0,
                totalUnsubscribed: 0,
                totalOptedOut: 0
            }
        }

        if(/* !Array.isArray(dealType) && (dealType).toLowerCase() !== "broker" && */
            curr.status.toLowerCase() === "won" &&
            // !dealCloseOverride &&
            curr.closeTimestamp >= sDateMillis &&
            curr.closeTimestamp <= eDateMillis){
            prev.sources[source].mediums[medium].revenue += (curr?.revenue ?? 0) - (curr?.brokerCommission ?? 0)
            prev.sources[source].mediums[medium].unitSales += numberOfUnitsSold  
            
            prev.dealTypes[dealType].revenue += (curr?.revenue ?? 0) - (curr?.brokerCommission ?? 0)         
            prev.dealTypes[dealType].unitSales += numberOfUnitsSold

            prev.revenue += (curr?.revenue ?? 0) - (curr?.brokerCommission ?? 0)

            prev.brokerCommission += curr?.brokerCommission ?? 0
            prev.sources[source].brokerCommission += curr?.brokerCommission ?? 0   
            prev.dealTypes[dealType].brokerCommission += curr?.brokerCommission ?? 0      
            prev.leadOwners[leadOwnerName].brokerCommission += curr?.brokerCommission ?? 0    

            prev.sources[source].mediums[medium].brokerCommission += curr?.brokerCommission ?? 0  
        }

        const ts = DateTime.fromMillis(curr.timestamp)

        if(curr.signedTimestamp &&
            curr.signedTimestamp >= sDateMillis &&
            curr.signedTimestamp <= eDateMillis){
            prev.totalLeadToAppDays += DateTime.fromMillis(curr.signedTimestamp).diff(ts, "days").toObject().days
            prev.totalLeadToApp ++
            prev.totalAppsSigned ++
            
            prev.sources[source].campaigns[campaign].totalAppsSigned ++
            prev.sources[source].mediums[medium].campaigns[campaign].totalAppsSigned ++

            prev.personas[persona].totalAppsSigned ++

            if(prev.dailyAppsSigned[DateTime.fromMillis(curr.signedTimestamp).toISODate()]){
                prev.dailyAppsSigned[DateTime.fromMillis(curr.signedTimestamp).toISODate()].y += 1
            }

            if(appSignedFinanciallyQualified){
                prev.sources[source].mediums[medium].totalFinanciallyQualifiedAppSigned ++
                prev.sources[source].campaigns[campaign].totalFinanciallyQualifiedAppSigned ++

                prev.totalFinanciallyQualifiedAppSigned ++
                
                prev.sources[source].totalFinanciallyQualifiedAppSigned ++
            }
        }

        if(curr.fddSignedTimestamp &&
            curr.fddSignedTimestamp >= sDateMillis &&
            curr.fddSignedTimestamp <= eDateMillis){
            prev.totalFDDSigned ++
            prev.sources[source].campaigns[campaign].totalFDDSigned ++
            prev.sources[source].mediums[medium].campaigns[campaign].totalFDDSigned ++

            if(prev.dailyFDDSigned[DateTime.fromMillis(curr.fddSignedTimestamp).toISODate()]){
                prev.dailyFDDSigned[DateTime.fromMillis(curr.fddSignedTimestamp).toISODate()].y += 1
            }
        }
        
        if(curr.closeTimestamp && 
            curr.status.toLowerCase() === "won" &&
            // !dealCloseOverride &&
            curr.closeTimestamp >= sDateMillis &&
            curr.closeTimestamp <= eDateMillis){         
            prev.totalLeadToDealDays += DateTime.fromMillis(curr.closeTimestamp).diff(ts, "days").toObject().days
            prev.totalLeadToDeal ++
            prev.unitSales += numberOfUnitsSold
        }
        
        if(curr.bookingCreatedAtTimestamp &&
            curr.bookingCreatedAtTimestamp >= sDateMillis &&
            curr.bookingCreatedAtTimestamp <= eDateMillis){
            
            if(bookingType === "SMS"){
                prev.totalLeadToBookingCreatedAtMinsSMS.push(Math.round(DateTime.fromMillis(curr.bookingCreatedAtTimestamp).diff(ts, "minutes").toObject().minutes))
                prev.totalLeadToBookingCreatedAtSMS ++  
                
                if(financiallyQualified){
                    prev.totalFinQualLeadToBookingCreatedAtMinsSMS.push(Math.round(DateTime.fromMillis(curr.bookingCreatedAtTimestamp).diff(ts, "minutes").toObject().minutes))
                    prev.totalFinQualLeadToBookingCreatedAtSMS ++
                }
            }  else {
                prev.totalLeadToBookingCreatedAtOther ++
                prev.totalLeadToBookingCreatedAtMinsOther.push(Math.round(DateTime.fromMillis(curr.bookingCreatedAtTimestamp).diff(ts, "minutes").toObject().minutes))
            }

            prev.totalLeadToBookingCreatedAtMins.push(Math.round(DateTime.fromMillis(curr.bookingCreatedAtTimestamp).diff(ts, "minutes").toObject().minutes))
            prev.totalLeadToBookingCreatedAt ++
            
            if(financiallyQualified){
                prev.totalFinQualLeadToBookingCreatedAtMins.push(Math.round(DateTime.fromMillis(curr.bookingCreatedAtTimestamp).diff(ts, "minutes").toObject().minutes))
                prev.totalFinQualLeadToBookingCreatedAt ++
            }
        }

        if(curr.bookingCreatedAtTimestamp &&
            curr.bookingCreatedAtTimestamp >= sDateMillis &&
            curr.bookingCreatedAtTimestamp <= eDateMillis){
            if(bookingType === "SMS"){

                prev.totalContacted ++
                prev.sources[source].totalContacted ++
                prev.personas[persona].totalContacted ++
                
                prev.sources[source].campaigns[campaign].totalContacted ++
                
                prev.leadOwners[leadOwnerName].totalContacted ++
                
                if(curr.contactedTimestamp){
                    prev.totalLeadToContactedDays += DateTime.fromMillis(curr.contactedTimestamp).diff(ts, "days").toObject().days
                }
            }

            prev.totalContactedAllTypes ++
            prev.sources[source].totalContactedAllTypes ++
            prev.personas[persona].totalContactedAllTypes ++
            prev.personas[persona].totalBookingsAllTypes ++
            
            prev.sources[source].campaigns[campaign].totalContactedAllTypes ++
            
            prev.leadOwners[leadOwnerName].totalContactedAllTypes ++
            
            if(prev.dailyBookings[DateTime.fromMillis(curr.bookingCreatedAtTimestamp).toISODate()]){
                prev.dailyBookings[DateTime.fromMillis(curr.bookingCreatedAtTimestamp).toISODate()].y += 1
            }

            if(curr.bookingKept){
                if(bookingType === "SMS"){
                    
                    prev.totalBookingsKept ++
                    prev.personas[persona].totalBookingsKept ++

                    prev.sources[source].campaigns[campaign].totalBookingsKept ++
                    prev.leadOwners[leadOwnerName].totalBookingsKept ++
        
                    if(curr.rescheduledTimestamp &&
                        curr.rescheduledTimestamp >= sDateMillis &&
                        curr.rescheduledTimestamp <= eDateMillis){
                        // if(prev.dailyBookingsKept[DateTime.fromMillis(curr.rescheduledTimestamp).toISODate()]){
                        //     prev.dailyBookingsKept[DateTime.fromMillis(curr.rescheduledTimestamp).toISODate()].y += 1
                        // }
                    }
                }

                prev.totalBookingsKeptAllTypes ++
                prev.personas[persona].totalBookingsKeptAllTypes ++

                prev.sources[source].campaigns[campaign].totalBookingsKeptAllTypes ++
                prev.leadOwners[leadOwnerName].totalBookingsKeptAllTypes ++

                if(prev.dailyBookingsKept[DateTime.fromMillis(curr.bookingCreatedAtTimestamp).toISODate()]){
                    prev.dailyBookingsKept[DateTime.fromMillis(curr.bookingCreatedAtTimestamp).toISODate()].y += 1
                }
            }

            if(curr.signedTimestamp &&
                curr.signedTimestamp >= sDateMillis &&
                curr.signedTimestamp <= eDateMillis && 
                curr.bookingCreatedAtTimestamp >= curr.signedTimestamp){
                
                prev.sources[source].campaigns[campaign].totalAppsSignedBooked ++
                prev.totalAppsSignedBooked ++
            }
        }
        if((curr.engagementTimestamp &&
            curr.engagementTimestamp >= sDateMillis &&
            curr.engagementTimestamp <= eDateMillis) || (curr.engagedTimestamp &&
                curr.engagedTimestamp >= sDateMillis &&
                curr.engagedTimestamp <= eDateMillis)){
            prev.totalEngaged ++
            prev.sources[source].totalEngaged ++
            prev.personas[persona].totalEngaged ++
        }

        if(curr.cancelledCounter &&
            curr.cancelledTimestamp >= sDateMillis &&
            curr.cancelledTimestamp <= eDateMillis){
            prev.totalCancelled += curr.cancelledCounter
            prev.personas[persona].totalCancelled += curr.cancelledCounter

            prev.leadOwners[leadOwnerName].totalCancelled += curr.cancelledCounter
            prev.sources[source].campaigns[campaign].totalCancelled += curr.cancelledCounter
            
        } else if(curr.cancelledTimestamp &&
            curr.cancelledTimestamp >= sDateMillis &&
            curr.cancelledTimestamp <= eDateMillis){
            prev.totalCancelled ++
            prev.personas[persona].totalCancelled ++

            prev.leadOwners[leadOwnerName].totalCancelled ++

            prev.sources[source].campaigns[campaign].totalCancelled ++
        }

        if(curr.noShowCounter &&
            curr.noShowTimestamp >= sDateMillis &&
            curr.noShowTimestamp <= eDateMillis){
            prev.totalNoShow += curr.noShowCounter
            prev.personas[persona].totalNoShow += curr.noShowCounter

            prev.sources[source].campaigns[campaign].totalNoShow += curr.noShowCounter

        } else if(curr.noShowTimestamp &&
            curr.noShowTimestamp >= sDateMillis &&
            curr.noShowTimestamp <= eDateMillis){
            prev.totalNoShow ++
            prev.personas[persona].totalNoShow ++

            prev.leadOwners[leadOwnerName].totalNoShow ++

            prev.sources[source].campaigns[campaign].totalNoShow ++
        }

        if(curr.rescheduledCounter &&
            curr.rescheduledTimestamp >= sDateMillis &&
            curr.rescheduledTimestamp <= eDateMillis){
            prev.totalRescheduled += curr.rescheduledCounter
            prev.personas[persona].totalRescheduled += curr.rescheduledCounter

            prev.leadOwners[leadOwnerName].totalRescheduled += curr.rescheduledCounter

            prev.sources[source].campaigns[campaign].totalRescheduled += curr.rescheduledCounter
            // if(prev.dailyBookings[DateTime.fromMillis(curr.rescheduledTimestamp).toISODate()]){
            //     prev.dailyBookings[DateTime.fromMillis(curr.rescheduledTimestamp).toISODate()].y += curr.rescheduledCounter
            // }            
        } else if(curr.rescheduledTimestamp &&
            curr.rescheduledTimestamp >= sDateMillis &&
            curr.rescheduledTimestamp <= eDateMillis){
            prev.totalRescheduled ++
            prev.personas[persona].totalRescheduled ++

            prev.leadOwners[leadOwnerName].totalRescheduled ++
            
            prev.sources[source].campaigns[campaign].totalRescheduled ++
            // if(prev.dailyBookings[DateTime.fromMillis(curr.rescheduledTimestamp).toISODate()]){
            //     prev.dailyBookings[DateTime.fromMillis(curr.rescheduledTimestamp).toISODate()].y += 1
            // }
        }

        if(curr.discoveryDayTimestamp &&
            curr.discoveryDayTimestamp >= sDateMillis &&
            curr.discoveryDayTimestamp <= eDateMillis){
            prev.totalDiscovered ++
            prev.personas[persona].totalDiscovered ++
        }

        if(curr.timestamp >= sDateMillis &&
            curr.timestamp <= eDateMillis){

            prev.totalLeads ++
            
            prev.sources[source].campaigns[campaign].totalLeads ++
            prev.sources[source].mediums[medium].totalLeads ++
            prev.sources[source].mediums[medium].campaigns[campaign].totalLeads ++

            prev.sources[source].totalLeads ++

            prev.personas[persona].totalLeads ++

            prev.leadOwners[leadOwnerName].totalLeads ++

            if(dqReason){
                prev.totalDqReasonLeads ++

                prev.dqReasons[dqReason].totalLeads ++
                prev.dqReasons[dqReason].sources[source].totalLeads ++
                prev.dqReasons[dqReason].sources[source].mediums[medium].totalLeads ++
                
                prev.dqReasons[dqReason].dealValue += curr?.revenue ?? 0
                prev.dqReasons[dqReason].sources[source].dealValue += curr?.revenue ?? 0
                prev.dqReasons[dqReason].sources[source].mediums[medium].dealValue += curr?.revenue ?? 0

                prev.sources[source].mediums[medium].totalDqReasonLeads ++
            }
            
            if(financiallyQualified){

                prev.sources[source].mediums[medium].totalFinanciallyQualifiedLeads ++
                prev.sources[source].campaigns[campaign].totalFinanciallyQualifiedLeads ++

                prev.totalFinanciallyQualifiedLeads ++
                
                prev.sources[source].totalFinanciallyQualifiedLeads ++

                if(prev.dailyFinanciallyQualified[ts.toISODate()]){
                    prev.dailyFinanciallyQualified[ts.toISODate()].y += 1
                }
            }

            if(curr.smsIneligible){
                prev.totalLeadsIneligible ++
            }

            if(curr.unsubscribed){
                prev.totalUnsubscribed ++

                prev.sources[source].totalUnsubscribed ++
                prev.sources[source].campaigns[campaign].totalUnsubscribed ++
                prev.sources[source].mediums[medium].campaigns[campaign].totalUnsubscribed ++

                prev.personas[persona].totalUnsubscribed ++

                prev.leadOwners[leadOwnerName].totalUnsubscribed ++
            }

            if(curr.optedOut){
                prev.totalOptedOut ++

                prev.sources[source].totalOptedOut ++
                prev.sources[source].campaigns[campaign].totalOptedOut ++
                prev.sources[source].mediums[medium].campaigns[campaign].totalOptedOut ++

                prev.personas[persona].totalOptedOut ++

                prev.leadOwners[leadOwnerName].totalOptedOut ++
            }

             
            if(territoryStatus === "Available"){
                prev.totalOpenTerritories ++
                prev.sources[source].campaigns[campaign].totalOpenTerritories ++
            } else if(territoryStatus === "Unavailable"){
                prev.totalClosedTerritories ++
                prev.sources[source].campaigns[campaign].totalClosedTerritories ++
            } else if(territoryStatus === "Pending"){
                prev.totalPendingTerritories ++
                prev.sources[source].campaigns[campaign].totalPendingTerritories ++
            } else if(territoryStatus === "Unknown"){
                prev.totalUnknownTerritories ++
                prev.sources[source].campaigns[campaign].totalUnknownTerritories ++
            }

            if(adjustedTerritory){
                prev.totalAdjustedTerritories ++
                prev.sources[source].campaigns[campaign].totalAdjustedTerritories ++
            }


            if(prev.dailyLeads[DateTime.fromMillis(curr.timestamp).toISODate()]){
                prev.dailyLeads[DateTime.fromMillis(curr.timestamp).toISODate()].y += 1
            }
                
        }

        if(curr.aiInitEngagementTimestamp && 
            !curr.smsIneligible &&
            curr.aiInitEngagementTimestamp >= sDateMillis &&
            curr.aiInitEngagementTimestamp <= eDateMillis){
            prev.totalAIEngaged ++
            prev.personas[persona].totalAIEngaged ++
        }

        // if(idx === arr.length - 1){
        //     prev.dailyBookings = Object.values(prev.dailyBookings)
        // }

        return prev
    }, {
        sources: {},
        personas: {},
        dqReasons: {},
        dailyLeads,
        dailyBookings,
        dailyBookingsKept,
        dailyFinanciallyQualified,
        dailyAppsSigned,
        dailyFDDSigned,
        dealTypes: {},
        revenue: 0,
        totalLeadToApp: 0,
        totalLeadToAppDays: 0,
        totalLeadToContactedDays: 0,
        totalLeadToDeal: 0,
        totalLeadToDealDays: 0,
        unitSales: 0,
        totalEngaged: 0,
        totalContacted: 0,
        totalContactedAllTypes: 0,
        totalAppsSigned: 0,
        totalAppsSignedBooked: 0,
        totalFDDSigned: 0,
        totalRescheduled: 0,
        totalCancelled: 0,
        totalNoShow: 0,
        totalDiscovered: 0,
        totalBookingsKept: 0,
        totalBookingsKeptAllTypes: 0,
        totalFinanciallyQualifiedLeads: 0,
        totalFinanciallyQualifiedAppSigned: 0,
        totalLeads: 0,
        totalLeadsIneligible: 0,
        totalLeadsEligible: 0,
        totalInclusiveBookings: 0,
        totalOpenTerritories: 0,
        totalAdjustedTerritories: 0,
        totalClosedTerritories: 0,
        totalPendingTerritories: 0,
        totalUnknownTerritories: 0,
        totalAIEngaged: 0,
        totalDqReasonLeads: 0,
        totalLeadToBookingCreatedAtMins: [],
        totalLeadToBookingCreatedAt: 0,
        totalInEligibleLeadToBookingCreatedAt: 0,
        totalEligibleLeadToBookingCreatedAtMins: [],
        totalEligibleLeadToBookingCreatedAt: 0,
        totalFinQualLeadToBookingCreatedAtMins: [],
        totalFinQualLeadToBookingCreatedAt: 0,
        totalFinQualLeadToBookingCreatedAtMinsSMS: [],
        totalFinQualLeadToBookingCreatedAtSMS: 0,
        totalLeadToBookingCreatedAtMinsSMS: [],
        totalLeadToBookingCreatedAtSMS: 0,
        totalLeadToBookingCreatedAtMinsOther: [],
        totalLeadToBookingCreatedAtOther: 0,
        totalUnsubscribed: 0,
        totalOptedOut: 0,
        leadOwners: {},
        brokerCommission: 0
    })

    metricBySummary.totalBookings = metricBySummary.totalContacted + metricBySummary.totalRescheduled
    metricBySummary.totalBookingsAllTypes = metricBySummary.totalContactedAllTypes
    // metricBySummary.dailyBookings = Object.values(metricBySummary.dailyBookings)

    metricBySummary.totalLeadsEligible = metricBySummary.totalLeads - metricBySummary.totalLeadsIneligible - metricBySummary.totalOptedOut

    return {
        metrics,
        metricBySummary
    }
}

export const formatMonthlyLeads = (results, dailies=null) => {
    const now = DateTime.local()
    const currentYear = parseInt(now.toFormat("yyyy"))
    const lastEnabledMonth = parseInt(now.toFormat("yyyyLL"))

    let tempFranchiseSales = {}

    let summaryTotals = {
        years: {},
        totalUnits: 0,
        sourceTypeUnitTotals: {},
        sourceDealTypeUnitTotals: {}
    }
    
    results.forEach(metric => {
        let year = metric.yearMonth.toString().substring(0, 4)
        
        if(metric.leads){
            metric.leads.forEach(s => {
                let source = s?.metrics?.utm_source ? s.metrics?.utm_source : "(N/A)"
                let persona = s?.metrics?.persona ? s.metrics?.persona : "Main"

                let key = year + source
                let dealTypeKey = year + (s?.dealType ?? "(N/A)")
                let medium = (s.metrics?.utm_medium ?? "(N/A)")

                if(summaryTotals.years[year] === undefined){
                    summaryTotals.years[year] = {
                        metrics: {},
                        totalLeadToApp: 0,
                        totalLeadToAppDays: 0,
                        totalLeadToDeal: 0,
                        totalLeadToDealDays: 0,
                        totalDEI: 0,
                        totalLeads: 0,
                        totalUnits: 0,
                        totalWebsiteUnits: 0,
                        totalEngaged: 0,
                        totalContacted: 0,
                        totalAppsSigned: 0,
                        totalCancelled: 0,
                        totalNoShow: 0,
                        totalRescheduled: 0,
                        totalBookingsKept: 0,
                        totalDiscovered: 0,
                        totalBookings: 0
                    }
                    for(let i = 1; i <= 12; i ++){
                        let yMM = parseInt(year + (i > 9 ? "" + i: "0" + i));
                        
                        if(currentYear === parseInt(year) && lastEnabledMonth >= yMM){
                            summaryTotals.years[year].metrics[yMM] = { 
                                unitSales: 0, 
                                revenue: 0, 
                                totalContacted: 0, 
                                sources: {},
                                personas: {}
                            }
                        } else if(currentYear !== parseInt(year)) {
                            summaryTotals.years[year].metrics[yMM] = { 
                                unitSales: 0, 
                                revenue: 0, 
                                totalContacted: 0, 
                                sources: {},
                                personas: {}
                            }
                        }
                    }
                    
                }

                if(summaryTotals.years[year].metrics[metric.yearMonth]){
                    if(s.status.toLowerCase() === "won"){
                        summaryTotals.years[year].metrics[metric.yearMonth].unitSales ++
                        summaryTotals.years[year].metrics[metric.yearMonth].revenue += s.revenue ? parseInt(s.revenue) : 0
                    }
                    
                    if(summaryTotals.years[year].metrics[metric.yearMonth].sources[source] === undefined){
                        summaryTotals.years[year].metrics[metric.yearMonth].sources[source] = {
                            totalContacted: 0
                        }
                    }
                    if(summaryTotals.years[year].metrics[metric.yearMonth].personas[persona] === undefined){
                        summaryTotals.years[year].metrics[metric.yearMonth].personas[persona] = {
                            totalEngaged: 0,
                            totalContacted: 0,
                            totalBookings: 0,
                            totalAppsSigned: 0,
                            totalCancelled: 0,
                            totalNoShow: 0,
                            totalRescheduled: 0,
                            totalBookingsKept: 0,
                            totalDiscovered: 0,
                            totalLeads: 0
                        }
                    }

                    if(s.bookingCreatedAtTimestamp){
                        summaryTotals.years[year].metrics[metric.yearMonth].totalContacted ++
                        summaryTotals.years[year].metrics[metric.yearMonth].sources[source].totalContacted ++
                        summaryTotals.years[year].metrics[metric.yearMonth].personas[persona].totalContacted ++
                    }
                }

                if(summaryTotals.sourceTypeUnitTotals[key] === undefined){
                    summaryTotals.sourceTypeUnitTotals[key] = {
                        key,
                        year: parseInt(year),
                        source: s.metrics?.utm_source ?? "(N/A)",
                        revenue: 0,
                        unitSales: 0,
                        mediums: {}
                    }
                }

                if(summaryTotals.sourceTypeUnitTotals[key].mediums[medium] === undefined){
                    summaryTotals.sourceTypeUnitTotals[key].mediums[medium] = {
                        revenue: 0,
                        unitSales: 0
                    }
                }

                if((s?.dealType ?? "(N/A)").toLowerCase() !== "broker"){
                    if(s.status.toLowerCase() === "won"){
                        summaryTotals.sourceTypeUnitTotals[key].mediums[medium].revenue += s.revenue ? parseInt(s.revenue) : 0
                        summaryTotals.sourceTypeUnitTotals[key].mediums[medium].unitSales ++
                    }
                }

                if(summaryTotals.sourceDealTypeUnitTotals[dealTypeKey] === undefined){
                    summaryTotals.sourceDealTypeUnitTotals[dealTypeKey] = {
                        key,
                        year: parseInt(year),
                        dealType: s?.dealType ?? "(N/A)",
                        revenue: 0,
                        unitSales: 0
                    }
                }

                if(s.status.toLowerCase() === "won"){
                    summaryTotals.sourceTypeUnitTotals[key].revenue += s.revenue ? parseInt(s.revenue) : 0
                    summaryTotals.sourceTypeUnitTotals[key].unitSales ++

                    summaryTotals.sourceDealTypeUnitTotals[dealTypeKey].revenue += s.revenue ? parseInt(s.revenue) : 0
                    summaryTotals.sourceDealTypeUnitTotals[dealTypeKey].unitSales ++
                }
                
                if(tempFranchiseSales[key] === undefined){
                    tempFranchiseSales[key] = {
                        key,
                        source: s.metrics?.utm_source,
                        year: parseInt(year),
                        display: capitalize(s.metrics?.utm_source ?? "(N/A)"),
                        metrics: {}
                    }
                }

                if(tempFranchiseSales[key].metrics[metric.yearMonth] === undefined){
                    tempFranchiseSales[key].metrics[metric.yearMonth] = {
                        revenue: 0,
                        unitSales: 0,
                        leads: []
                    }
                }

                
                if(s.status.toLowerCase() === "won"){
                    tempFranchiseSales[key].metrics[metric.yearMonth].revenue += s.revenue ? parseInt(s.revenue) : 0
                    tempFranchiseSales[key].metrics[metric.yearMonth].unitSales ++
                }

                tempFranchiseSales[key].metrics[metric.yearMonth].leads.push(s)


                if(s.status.toLowerCase() === "won"){
                    if(s?.metrics?.minority) {
                        summaryTotals.years[year].totalDEI += 1
                    }

                    summaryTotals.years[year].totalUnits ++
                }

                if(s.engagementTimestamp){
                    summaryTotals.years[year].totalEngaged ++
                }
                if(s.bookingCreatedAtTimestamp){
                    summaryTotals.years[year].totalContacted ++
                    summaryTotals.years[year].totalBookings ++
                }
                if(s.signedTimestamp){
                    summaryTotals.years[year].totalAppsSigned ++
                }
                if(s.rescheduledTimestamp){
                    summaryTotals.years[year].totalRescheduled ++
                    summaryTotals.years[year].totalBookings ++
                }
                if(s.noShowTimestamp){
                    summaryTotals.years[year].totalNoShow ++
                }
                if(s.cancelledTimestamp){
                    summaryTotals.years[year].totalCancelled ++
                }
                if(s.discoveryDayTimestamp){
                    summaryTotals.years[year].totalDiscovered ++
                }
                if(s.bookingKept){
                    summaryTotals.years[year].totalBookingsKept ++
                }

                if((s?.dealType ?? "(N/A)").toLowerCase() !== "broker"){
                    if(s.status.toLowerCase() === "won"){
                        summaryTotals.years[year].totalWebsiteUnits ++
                    }
                }
            })
        }

        if(metric?.totalLeadToApp > 0){
            summaryTotals.years[year].totalLeadToApp += metric?.totalLeadToApp
            summaryTotals.years[year].totalLeadToAppDays += metric?.totalLeadToAppDays
        }
        if(metric?.totalLeadToDeal > 0){
            summaryTotals.years[year].totalLeadToDeal += metric?.totalLeadToDeal
            summaryTotals.years[year].totalLeadToDealDays += metric?.totalLeadToDealDays            
        }
    })

    if(dailies?.metricBySummary){
        Object.keys(dailies?.metricBySummary?.dealTypes).forEach(dealType => {
            let key = currentYear + (dealType)
            let { revenue, unitSales } = dailies?.metricBySummary?.dealTypes[dealType]

            if(summaryTotals.sourceDealTypeUnitTotals[key] === undefined){
                summaryTotals.sourceDealTypeUnitTotals[key] = {
                    key,
                    year: parseInt(currentYear),
                    dealType,
                    revenue: 0,
                    unitSales: 0
                }
            }
            summaryTotals.sourceDealTypeUnitTotals[key].revenue += revenue ? parseInt(revenue) : 0
            summaryTotals.sourceDealTypeUnitTotals[key].unitSales += unitSales
        })

        Object.keys(dailies?.metricBySummary?.sources).forEach(source => {
            let key = currentYear + (source)
            let { revenue, unitSales, mediums } = dailies?.metricBySummary?.sources[source]
            if(summaryTotals.years[currentYear] === undefined){
                summaryTotals.years[currentYear] = {
                    metrics: {},
                    totalDEI: 0,
                    totalUnits: 0,
                    totalLeadToApp: 0,
                    totalLeadToAppDays: 0,
                    totalLeadToDeal: 0,
                    totalLeadToDealDays: 0,
                    totalEngaged: 0,
                    totalContacted: 0,
                    totalBookings: 0,
                    totalAppsSigned: 0,
                    totalCancelled: 0,
                    totalNoShow: 0,
                    totalRescheduled: 0,
                    totalDiscovered: 0,
                    totalBookingsKept: 0
                }
                for(let i = 1; i <= 12; i ++){
                    let yMM = parseInt(currentYear + (i > 9 ? "" + i: "0" + i));
                    
                    summaryTotals.years[currentYear].metrics[yMM] = { unitSales: 0, revenue: 0}
                }                
            }

            if(summaryTotals.years[currentYear].metrics[lastEnabledMonth]){
                summaryTotals.years[currentYear].metrics[lastEnabledMonth].unitSales += unitSales
                summaryTotals.years[currentYear].metrics[lastEnabledMonth].revenue += revenue ? parseInt(revenue) : 0
            }

            if(summaryTotals.sourceTypeUnitTotals[key] === undefined){
                summaryTotals.sourceTypeUnitTotals[key] = {
                    key,
                    year: parseInt(currentYear),
                    source,
                    revenue: 0,
                    unitSales: 0,
                    mediums: {}
                }
            }

            if(tempFranchiseSales[key] === undefined){
                tempFranchiseSales[key] = {
                    key,
                    source,
                    year: parseInt(currentYear),
                    yearMonth: lastEnabledMonth,
                    display: capitalize(source),
                    metrics: {}
                }
            }

            if(tempFranchiseSales[key].metrics[lastEnabledMonth] === undefined){
                tempFranchiseSales[key].metrics[lastEnabledMonth] = {
                    revenue: 0,
                    unitSales: 0,
                    leads: []
                }
            }

            tempFranchiseSales[key].metrics[lastEnabledMonth].revenue += revenue ? parseInt(revenue) : 0
            tempFranchiseSales[key].metrics[lastEnabledMonth].unitSales += unitSales

            summaryTotals.sourceTypeUnitTotals[key].revenue += revenue ? parseInt(revenue) : 0
            summaryTotals.sourceTypeUnitTotals[key].unitSales += unitSales

            Object.keys(mediums).forEach(medium => {
                if(summaryTotals.sourceTypeUnitTotals[key].mediums[medium] === undefined){
                    summaryTotals.sourceTypeUnitTotals[key].mediums[medium] = {
                        revenue: 0,
                        unitSales: 0
                    }
                }

                summaryTotals.sourceTypeUnitTotals[key].mediums[medium].revenue += mediums[medium].revenue ?? 0
                summaryTotals.sourceTypeUnitTotals[key].mediums[medium].unitSales += mediums[medium].unitSales
            })
        })


        if(dailies?.metricBySummary?.totalLeadToApp > 0){
            summaryTotals.years[currentYear].totalLeadToApp += dailies?.metricBySummary?.totalLeadToApp
            summaryTotals.years[currentYear].totalLeadToAppDays += dailies?.metricBySummary?.totalLeadToAppDays
        }
        if(dailies?.metricBySummary?.totalLeadToDeal > 0){
            summaryTotals.years[currentYear].totalLeadToDeal += dailies?.metricBySummary?.totalLeadToDeal
            summaryTotals.years[currentYear].totalLeadToDealDays += dailies?.metricBySummary?.totalLeadToDealDays            
        }
        if(dailies?.metricBySummary?.unitSales > 0){
            summaryTotals.years[currentYear].totalUnits += dailies?.metricBySummary?.unitSales ?? 0
        }
    }

    if(dailies?.metrics){
        dailies.metrics?.forEach(lead => {
            let source = lead?.metrics?.utm_source ? lead.metrics?.utm_source : "(N/A)"
            let key = currentYear + source

            if(lead.status.toLowerCase() === "won"){
                if(lead?.metrics?.minority) {
                    summaryTotals.years[currentYear].totalDEI += 1
                }

                if((lead?.dealType ?? "(N/A)").toLowerCase() !== "broker"){
                    summaryTotals.years[currentYear].totalWebsiteUnits ++
                }
                tempFranchiseSales[key].metrics[lastEnabledMonth].leads.push(lead)
            }
        })
    }
    
    summaryTotals.sourceTypeUnitTotalsObj = summaryTotals.sourceTypeUnitTotals
    summaryTotals.sourceTypeUnitTotals = Object.values(summaryTotals.sourceTypeUnitTotals)
    summaryTotals.sourceDealTypeUnitTotals = Object.values(summaryTotals.sourceDealTypeUnitTotals)

    return {
        franchiseSales: Object.values(tempFranchiseSales),
        summaryTotals
    }
}

export const summarizeMonthlyLeads = (monthMetric) => {
    const defaultObj = {
        units: 0,
        revenue: 0,
        avgRevenuePerSale: 0
    }
    const summary = monthMetric.franchiseSales ? monthMetric.franchiseSales.reduce((prev, curr, idx, arr) => {
        prev.units += parseInt(curr.value)
        prev.revenue += (parseInt(curr.value) * curr.revenuePerUnit)
        
        if(idx === arr.length - 1){
            prev.avgRevenuePerSale = prev.revenue / prev.units
        }
        
        return prev
    }, {
        ...defaultObj
    }) : {
        ...defaultObj
    }
    
    return summary
}

export const formatLeads = (results, dailies = null, observableYear=DateTime.local().toFormat("yyyy")) => {
    const now = DateTime.local()
    const currentYear = parseInt(observableYear)
    const lastEnabledMonth = parseInt(`${observableYear}${now.toFormat("MM")}`)

    let tempFranchiseSales = {}
    const leadIdMap = {}

    let summaryTotals = {
        years: {},
        totalUnits: 0,
        sourceTypeUnitTotals: {},
        sourceDealTypeUnitTotals: {}
    }

    if(results.length > 0){
        results.forEach(metric => {
            let year = metric.yearMonth.toString().substring(0, 4)
            if(summaryTotals.years[year] === undefined){
                summaryTotals.years[year] = {
                    metrics: {},
                    sources: {},
                    personas: {},
                    revenue: 0,
                    totalLeadToApp: 0,
                    totalLeadToAppDays: 0,
                    totalLeadToContacted: 0,
                    totalLeadToContactedDays: 0,
                    totalLeadToDeal: 0,
                    totalLeadToDealDays: 0,
                    totalDEI: 0,
                    totalUnits: 0,
                    totalWebsiteUnits: 0,
                    totalContacted: 0,
                    totalContactedAllTypes: 0,
                    totalAppsSigned: 0,
                    totalFDDSigned: 0,
                    totalLeads: 0,
                    totalEngaged: 0,
                    totalBookings: 0,
                    totalBookingsAllTypes: 0,
                    totalCancelled: 0,
                    totalNoShow: 0,
                    totalRescheduled: 0,
                    totalDiscovered: 0,
                    unitSales: 0,
                    totalBookingsKept: 0,
                    totalBookingsKeptAllTypes: 0,
                    totalFinanciallyQualifiedLeads: 0,
                    totalOpenTerritories: 0,
                    totalClosedTerritories: 0,
                    totalAdjustedTerritories: 0,
                    totalPendingTerritories: 0,
                    totalUnknownTerritories: 0,
                    totalAIEngaged: 0,
                    totalUnsubscribed: 0,
                    totalOptedOut: 0,
                    totalLeadToBookingCreatedAt: 0,
                    totalLeadToBookingCreatedAtMins: [],
                    totalLeadToBookingCreatedAtSMS: 0,
                    totalLeadToBookingCreatedAtMinsSMS: [],
                    totalLeadToBookingCreatedAtOther: 0,
                    totalLeadToBookingCreatedAtMinsOther: []
                }
                for(let i = 1; i <= 12; i ++){
                    let yMM = parseInt(year + (i > 9 ? "" + i: "0" + i));
                    
                    if(currentYear === parseInt(year) /* && lastEnabledMonth >= yMM */){
                        summaryTotals.years[year].metrics[yMM] = { 
                            unitSales: 0, revenue: 0, totalLeads: 0,
                            totalContacted: 0, totalContactedAllTypes: 0, 
                            totalBookings: 0, totalBookingsAllTypes: 0,
                            sources: {}, personas: {}, 
                            totalBookingsKept: 0, totalBookingsKeptAllTypes: 0, 
                            totalAppsSigned: 0, totalFDDSigned: 0, totalRescheduled: 0,
                            totalNoShow: 0, totalCancelled: 0, totalFinanciallyQualifiedLeads: 0,
                            totalDiscovered: 0, totalOpenTerritories: 0, totalAdjustedTerritories: 0,
                            totalClosedTerritories: 0, totalPendingTerritories: 0, totalUnknownTerritories: 0,
                            totalAIEngaged: 0,
                            totalLeadToBookingCreatedAt: 0,
                            totalUnsubscribed: 0,
                            totalOptedOut: 0,
                            totalLeadToBookingCreatedAtMins: [],
                            totalLeadToBookingCreatedAtMinsSMS: [],
                            totalLeadToBookingCreatedAtSMS: 0,
                            totalLeadToBookingCreatedAtOther: 0,
                            totalLeadToBookingCreatedAtMinsOther: []
                        }
                    } else if(currentYear !== parseInt(year)) {
                        summaryTotals.years[year].metrics[yMM] = { 
                            unitSales: 0, revenue: 0, totalLeads: 0,
                            totalContacted: 0, totalContactedAllTypes: 0, 
                            totalBookings: 0, totalBookingsAllTypes: 0, 
                            sources: {}, personas: {}, 
                            totalBookingsKept: 0, totalBookingsKeptAllTypes: 0, 
                            totalAppsSigned: 0, totalFDDSigned: 0, totalRescheduled: 0,
                            totalNoShow: 0, totalCancelled: 0, totalFinanciallyQualifiedLeads: 0,
                            totalDiscovered: 0, totalOpenTerritories: 0, totalAdjustedTerritories: 0,
                            totalClosedTerritories: 0, totalPendingTerritories: 0, totalUnknownTerritories: 0,
                            totalAIEngaged: 0,
                            totalLeadToBookingCreatedAt: 0,
                            totalUnsubscribed: 0,
                            totalOptedOut: 0,
                            totalLeadToBookingCreatedAtMins: [],
                            totalLeadToBookingCreatedAtMinsSMS: [],
                            totalLeadToBookingCreatedAtSMS: 0,
                            totalLeadToBookingCreatedAtOther: 0,
                            totalLeadToBookingCreatedAtMinsOther: []
                        }
                    }
                }
                
            }
            if(metric?.leads && metric?.leads?.length > 0){
                metric.leads.forEach(s => {
                    let dealTypeKey = year + (s?.dealType ?? "(N/A)")
                    
                    if(leadIdMap[s.leadId] === undefined){
                        leadIdMap[s.leadId] = {...s, yearMonth: metric.yearMonth, dealTypeKey}
                    } else if(leadIdMap[s.leadId]){
                        if(s.timestampUpdated > leadIdMap[s.leadId].timestampUpdated){
                            leadIdMap[s.leadId] = {...s, yearMonth: metric.yearMonth, dealTypeKey}
                        }
                    }

                    // if(summaryTotals.years[year].metrics[metric.yearMonth]){
                    //     if(s.status.toLowerCase() === "won"){
                    //         summaryTotals.years[year].metrics[metric.yearMonth].unitSales ++
                    //         summaryTotals.years[year].metrics[metric.yearMonth].revenue += parseInt(s.revenue)
                    //     }
                    // }

                })
            } else if(metric?.metricBySummary){
                summaryTotals = combineObjects(summaryTotals, metric.metricBySummary, LEAD_SUMMARY_TPL)

                Object.keys(metric.metricBySummary.sources).forEach(source => {
                    let item = metric.metricBySummary.sources[source]
                    let sKey = year + source

                    if(summaryTotals.sourceTypeUnitTotals[sKey] === undefined){
                        summaryTotals.sourceTypeUnitTotals[sKey] = {
                            key: sKey,
                            year: parseInt(year),
                            source: source ?? "(N/A)",
                            revenue: 0,
                            unitSales: 0,
                            totalLeads: 0,
                            totalFinanciallyQualifiedLeads: 0,
                            totalAppsSigned: 0,
                            totalDiscovered: 0,
                            totalContacted: 0,
                            mediums: {}
                        }
                    }
                    
                    summaryTotals.sourceTypeUnitTotals[sKey].revenue += item.revenue ? parseInt(item.revenue) : 0
                    summaryTotals.sourceTypeUnitTotals[sKey].unitSales += item.unitSales

                    summaryTotals.sourceTypeUnitTotals[sKey].totalLeads += item?.totalLeads ?? 0
                    summaryTotals.sourceTypeUnitTotals[sKey].totalFinanciallyQualifiedLeads += item?.totalFinanciallyQualifiedLeads ?? 0
                    summaryTotals.sourceTypeUnitTotals[sKey].totalContacted += item?.totalContacted ?? 0
                    summaryTotals.sourceTypeUnitTotals[sKey].totalAppsSigned += item?.totalAppsSigned ?? 0
                    summaryTotals.sourceTypeUnitTotals[sKey].totalDiscovered += item?.totalDiscovered ?? 0

                    // summaryTotals.sourceTypeUnitTotals[sKey] = combineObjects(summaryTotals.sourceTypeUnitTotals[sKey], item)

                    Object.keys(item.mediums).forEach(medium => {
                        let key = year + source + medium
                        let mediumItem = item.mediums[medium]
                        if(tempFranchiseSales[key] === undefined){
                            tempFranchiseSales[key] = {
                                key,
                                source,
                                medium,
                                year: parseInt(year),
                                display: capitalize(source !== "" ? source : "(N/A)"),
                                metrics: {}
                            }
                        }

                        if(tempFranchiseSales[key].metrics[parseInt(metric.yearMonth)] === undefined){
                            tempFranchiseSales[key].metrics[parseInt(metric.yearMonth)] = {
                                revenue: 0,
                                unitSales: 0,
                                leads: []
                            }
                        }
                        
                        tempFranchiseSales[key].metrics[parseInt(metric.yearMonth)].revenue += mediumItem.revenue ? parseInt(mediumItem.revenue) : 0
                        tempFranchiseSales[key].metrics[parseInt(metric.yearMonth)].unitSales += mediumItem.unitSales

                        if(summaryTotals.sourceTypeUnitTotals[sKey].mediums[medium] === undefined){
                            summaryTotals.sourceTypeUnitTotals[sKey].mediums[medium] = {
                                revenue: 0,
                                unitSales: 0,
                                totalLeads: 0,
                                totalEngaged: 0,
                                totalContacted: 0,
                                totalContactedAllTypes: 0, 
                                totalBookings: 0,
                                totalBookingsAllTypes: 0,
                                totalAppsSigned: 0,
                                totalFDDSigned: 0,
                                totalCancelled: 0,
                                totalNoShow: 0,
                                totalRescheduled: 0,
                                totalDiscovered: 0,
                                totalBookingsKept: 0,
                                totalBookingsKeptAllTypes: 0,
                                totalOpenTerritories: 0,
                                totalAdjustedTerritories: 0,
                                totalClosedTerritories: 0, 
                                totalPendingTerritories: 0, 
                                totalUnknownTerritories: 0,
                                totalFinanciallyQualifiedLeads: 0,
                                totalUnsubscribed: 0,
                                totalOptedOut: 0,
                            }
                        }

                        summaryTotals.sourceTypeUnitTotals[sKey].mediums[medium] = combineObjects(summaryTotals.sourceTypeUnitTotals[sKey].mediums[medium], mediumItem)                        
                             
                    })
                })
                    
                Object.keys(metric.metricBySummary.dealTypes).forEach(dealType => {
                    let dealTypeKey = year + (dealType ?? "(N/A)")
                    let item = metric.metricBySummary.dealTypes[dealType]
                    
                    if(summaryTotals.sourceDealTypeUnitTotals[dealTypeKey] === undefined){
                        summaryTotals.sourceDealTypeUnitTotals[dealTypeKey] = {
                            year: parseInt(year),
                            dealType: dealType ?? "(N/A)",
                            revenue: 0,
                            unitSales: 0
                        }
                    }
                    
                    summaryTotals.sourceDealTypeUnitTotals[dealTypeKey].revenue += item.revenue ? parseInt(item.revenue) : 0
                    summaryTotals.sourceDealTypeUnitTotals[dealTypeKey].unitSales += item.unitSales
                })
                
                summaryTotals.years[year] = combineObjects(summaryTotals.years[year], metric.metricBySummary, summaryTotals.years[year])
                summaryTotals.years[year].metrics[parseInt(metric.yearMonth)] = {...metric.metricBySummary} 
            }
        })
    }
    
    if(dailies){
        dailies.forEach(s => {
            let dealTypeKey = currentYear + (s?.dealType ?? "(N/A)")
            if(leadIdMap[s.leadId] === undefined){
                leadIdMap[s.leadId] = {...s, dealTypeKey, yearMonth: lastEnabledMonth}
            } else if(leadIdMap[s.leadId]){
                leadIdMap[s.leadId] = {...s, dealTypeKey, yearMonth: lastEnabledMonth}
            }            
        })
    }

    if(Object.values(leadIdMap).length > 0){
        const yearMillisMap = {
            [currentYear]: {
                tsStart: DateTime.fromFormat(`${currentYear}-01-01`, "yyyy-MM-dd").startOf("day").toMillis(),
                tsEnd: DateTime.fromFormat(`${currentYear}-12-31`, "yyyy-MM-dd").endOf("day").toMillis(),
            },
            [currentYear-1]: {
                tsStart: DateTime.fromFormat(`${currentYear-1}-01-01`, "yyyy-MM-dd").startOf("day").toMillis(),
                tsEnd: DateTime.fromFormat(`${currentYear-1}-12-31`, "yyyy-MM-dd").endOf("day").toMillis(),
            },
            [currentYear-2]: {
                tsStart: DateTime.fromFormat(`${currentYear-2}-01-01`, "yyyy-MM-dd").startOf("day").toMillis(),
                tsEnd: DateTime.fromFormat(`${currentYear-2}-12-31`, "yyyy-MM-dd").endOf("day").toMillis(),                
            }
        }


        const thisMonthStartTs = DateTime.fromFormat(lastEnabledMonth.toString(), "yyyyMM").startOf("month").toMillis()
        const thisMonthEndTs = DateTime.fromFormat(lastEnabledMonth.toString(), "yyyyMM").endOf("month").toMillis()

        Object.values(leadIdMap).forEach(lead => {
            let source = lead.metrics?.utm_source && lead.metrics?.utm_source !== "" ? lead.metrics?.utm_source : "(N/A)"
            let medium = lead.metrics?.utm_medium && lead.metrics?.utm_medium !== "" ? lead.metrics?.utm_medium : "(N/A)"
            
            if((!source || source === "(N/A)") && 
                lead?.metrics?.alt_utm_source && 
                lead?.metrics?.alt_utm_source !== ""){
                source = lead?.metrics?.alt_utm_source
            }
            if((!medium || medium === "(N/A)") && 
                lead?.metrics?.alt_utm_medium && 
                lead?.metrics?.alt_utm_medium !== ""){
                medium = lead?.metrics?.alt_utm_medium
            }

            let persona = lead.metrics?.persona && lead.metrics?.persona !== "" ? lead.metrics?.persona : "Main"
            let dqReason = (lead.metrics?.dqReason && lead.metrics?.dqReason !== "" ? lead.metrics?.dqReason : null)
            
            let financiallyQualified = lead?.metrics?.financiallyQualified ?? false
            let territoryStatus = lead?.metrics?.territoryStatus ? lead.metrics?.territoryStatus : ""
            let adjustedTerritory = lead?.metrics?.adjustedTerritory ? lead?.metrics?.adjustedTerritory : null

            let dealCloseOverride = lead?.metrics?.dealCloseOverride ? lead.metrics?.dealCloseOverride : false

            const unsubscribed = lead?.unsubscribed ?? false
            const optedOut = lead?.optedOut ?? false

            let year = lead.yearMonth.toString().substring(0, 4)
            let key = year + source
            let dealTypeKey = year + (lead?.dealType ? lead?.dealType : "(N/A)")
            
            let mKey = year + source + medium

            const ymStartTs = DateTime.fromFormat(lead.yearMonth.toString(), "yyyyMM").startOf("month").toMillis()
            const ymEndTs = DateTime.fromFormat(lead.yearMonth.toString(), "yyyyMM").endOf("month").toMillis()

            const startOfObserveYearMillis = yearMillisMap[parseInt(year)].tsStart
            const endOfObserveYearMillis = yearMillisMap[parseInt(year)].tsEnd

            const numberOfUnitsSold = lead?.metrics?.unitsSoldOverride && lead?.metrics?.unitsSoldOverride !== null ? lead?.metrics?.unitsSoldOverride : 1

            const bookingType = lead?.bookingType ? lead.bookingType : "SMS"

            const ts = DateTime.fromMillis(lead.timestamp)

            if(summaryTotals.years[year] === undefined){
                summaryTotals.years[year] = {
                    metrics: {},
                    dqReasons: {},
                    sources: {},
                    personas: {},
                    revenue: 0,
                    totalLeadToApp: 0,
                    totalLeadToAppDays: 0,
                    totalLeadToContacted: 0,
                    totalLeadToContactedDays: 0,
                    totalLeadToDeal: 0,
                    totalLeadToDealDays: 0,
                    totalDEI: 0,
                    totalUnits: 0,
                    totalWebsiteUnits: 0,
                    totalEngaged: 0,
                    totalContacted: 0,
                    totalContactedAllTypes: 0,
                    totalBookings: 0,
                    totalBookingsAllTypes: 0,
                    totalAppsSigned: 0,
                    totalFDDSigned: 0,
                    totalLeads: 0,
                    unitSales: 0,
                    totalRescheduled: 0,
                    totalNoShow: 0,
                    totalCancelled: 0,
                    totalDiscovered: 0,
                    totalBookingsKept: 0,
                    totalBookingsKeptAllTypes: 0,
                    totalFinanciallyQualifiedLeads: 0,
                    totalAIEngaged: 0,
                    totalDqReasonLeads: 0,
                    totalLeadToBookingCreatedAtMins: [],
                    totalLeadToBookingCreatedAt: 0,
                    totalLeadToBookingCreatedAtSMS: 0,
                    totalLeadToBookingCreatedAtMinsSMS: [],
                    totalLeadToBookingCreatedAtOther: 0,
                    totalLeadToBookingCreatedAtMinsOther: [],
                    totalUnsubscribed: 0,
                    totalOptedOut: 0,
                }                
                
                for(let i = 1; i <= 12; i ++){
                    let yMM = parseInt(year + (i > 9 ? "" + i: "0" + i));
                    
                    summaryTotals.years[year].metrics[yMM] = { 
                        unitSales: 0, revenue: 0, totalLeads: 0,
                        totalContacted: 0, totalContactedAllTypes: 0, 
                        totalBookings: 0, totalBookingsAllTypes: 0,
                        sources: {}, personas: {}, 
                        totalBookingsKept: 0, totalBookingsKeptAllTypes: 0, 
                        totalAppsSigned: 0, totalFDDSigned: 0, totalRescheduled: 0,
                        totalNoShow: 0, totalCancelled: 0, totalFinanciallyQualifiedLeads: 0,
                        totalDiscovered: 0, totalOpenTerritories: 0, totalAdjustedTerritories: 0,
                        totalClosedTerritories: 0, totalPendingTerritories: 0, totalUnknownTerritories: 0,
                        totalLeadToBookingCreatedAt: 0,
                        totalUnsubscribed: 0, totalOptedOut: 0,
                        totalLeadToBookingCreatedAtMins: [],
                        totalLeadToBookingCreatedAtMinsSMS: [],
                        totalLeadToBookingCreatedAtSMS: 0,
                        totalLeadToBookingCreatedAtOther: 0,
                        totalLeadToBookingCreatedAtMinsOther: []
                    }
                } 
            }

            if(summaryTotals.years[year].sources === undefined){
                summaryTotals.years[year].sources = {}
            }

            if(summaryTotals.years[year].personas === undefined){
                summaryTotals.years[year].personas = {}
            }

            if(summaryTotals.years[year].sources[source] === undefined){
                summaryTotals.years[year].sources[source] = {
                    totalContacted: 0
                }
            }

            if(summaryTotals.years[year].dqReasons === undefined){
                summaryTotals.years[year].dqReasons = {}
            }

            if(dqReason && summaryTotals.years[year].dqReasons[dqReason] === undefined){
                summaryTotals.years[year].dqReasons[dqReason] = {
                    sources: {},
                    dealValue: 0,
                    totalLeads: 0
                }
            }
    
            if(dqReason && summaryTotals.years[year].dqReasons[dqReason].sources[source] === undefined){
                summaryTotals.years[year].dqReasons[dqReason].sources[source] = {
                    mediums: {},
                    dealValue: 0,
                    totalLeads: 0
                }
            }
    
            if(dqReason && summaryTotals.years[year].dqReasons[dqReason].sources[source].mediums[medium] === undefined){
                summaryTotals.years[year].dqReasons[dqReason].sources[source].mediums[medium] = {
                    dealValue: 0,
                    totalLeads: 0
                }
            }
            
            if(summaryTotals.years[year].personas[persona] === undefined){
                summaryTotals.years[year].personas[persona] = {
                    totalEngaged: 0,
                    totalContacted: 0,
                    totalContactedAllTypes: 0,
                    totalBookingsAllTypes: 0,
                    totalRescheduled: 0,
                    totalNoShow: 0,
                    totalCancelled: 0,
                    totalDiscovered: 0,
                    totalBookingsKept: 0,
                    totalBookingsKeptAllTypes: 0,
                    totalLeads: 0,
                    totalAIEngaged: 0,
                    totalUnsubscribed: 0,
                    totalOptedOut: 0,
                    totalAppsSigned: 0
                }
            }

            if(summaryTotals.sourceTypeUnitTotals[key] === undefined){
                summaryTotals.sourceTypeUnitTotals[key] = {
                    key,
                    year: parseInt(year),
                    source: lead.metrics?.utm_source ?? "(N/A)",
                    revenue: 0,
                    unitSales: 0,
                    totalLeads: 0,
                    totalFinanciallyQualifiedLeads: 0,
                    totalAppsSigned: 0,
                    totalDiscovered: 0,
                    totalContacted: 0,
                    totalContactedAllTypes: 0,
                    mediums: {}
                }
            }

            if(summaryTotals.sourceTypeUnitTotals[key].mediums[medium] === undefined){
                summaryTotals.sourceTypeUnitTotals[key].mediums[medium] = {
                    revenue: 0,
                    unitSales: 0,
                    totalLeads: 0,
                    totalEngaged: 0,
                    totalContacted: 0,
                    totalContactedAllTypes: 0,
                    totalBookings: 0,
                    totalBookingsAllTypes: 0,
                    totalAppsSigned: 0,
                    totalFDDSigned: 0,
                    totalCancelled: 0,
                    totalNoShow: 0,
                    totalRescheduled: 0,
                    totalDiscovered: 0,
                    totalBookingsKept: 0,
                    totalBookingsKeptAllTypes: 0,
                    totalDqReasonLeads: 0,
                    totalDEI: 0,
                    totalUnsubscribed: 0,
                    totalOptedOut: 0,
                }
            }


            if(tempFranchiseSales[mKey] === undefined){
                tempFranchiseSales[mKey] = {
                    key: mKey,
                    source: source,
                    medium,
                    year: parseInt(year),
                    display: capitalize(source ?? "(N/A)"),
                    metrics: {}
                }
            }

            if(tempFranchiseSales[mKey].metrics[lead.yearMonth] === undefined){
                tempFranchiseSales[mKey].metrics[lead.yearMonth] = {
                    revenue: 0,
                    unitSales: 0,
                    leads: []
                }
            }
            
            if(summaryTotals.years[year].metrics[lead.yearMonth]){
                if(lead.status.toLowerCase() === "won" &&
                    lead.closeTimestamp &&
                    !dealCloseOverride &&
                    lead.closeTimestamp >= ymStartTs &&
                    lead.closeTimestamp <= ymEndTs){
                        
                    summaryTotals.years[year].metrics[lead.yearMonth].unitSales += numberOfUnitsSold
                    summaryTotals.years[year].metrics[lead.yearMonth].revenue += (lead.revenue ? parseInt(lead.revenue) : 0) - (lead.brokerCommission ? parseInt(lead.brokerCommission) : 0)

                    tempFranchiseSales[mKey].metrics[lead.yearMonth].revenue += (lead.revenue ? parseInt(lead.revenue) : 0) - (lead.brokerCommission ? parseInt(lead.brokerCommission) : 0)
                    tempFranchiseSales[mKey].metrics[lead.yearMonth].unitSales += numberOfUnitsSold
                }

                if(summaryTotals.years[year].metrics[lead.yearMonth].sources[source] === undefined){
                    summaryTotals.years[year].metrics[lead.yearMonth].sources[source] = {
                        totalContacted: 0,
                        totalBookings: 0
                    }
                }
                if(summaryTotals.years[year].metrics[lead.yearMonth].personas[persona] === undefined){
                    summaryTotals.years[year].metrics[lead.yearMonth].personas[persona] = {
                        totalEngaged: 0,
                        totalContacted: 0,
                        totalContactedAllTypes: 0,
                        totalBookings: 0,
                        totalBookingsAllTypes: 0,
                        totalAppsSigned: 0,
                        totalFDDSigned: 0,
                        totalCancelled: 0,
                        totalNoShow: 0,
                        totalRescheduled: 0,
                        totalBookingsKept: 0,
                        totalBookingsKeptAllTypes: 0,
                        totalDiscovered: 0,
                        totalLeads: 0,
                        totalAIEngaged: 0,
                        totalUnsubscribed: 0,
                        totalOptedOut: 0,
                    }
                }

                if(lead.bookingCreatedAtTimestamp && 
                    lead.bookingCreatedAtTimestamp >= ymStartTs &&
                    lead.bookingCreatedAtTimestamp <= ymEndTs){
                    
                    if(bookingType === "SMS"){
                        summaryTotals.years[year].metrics[lead.yearMonth].sources[source].totalContacted ++
                        summaryTotals.years[year].metrics[lead.yearMonth].personas[persona].totalContacted ++
                        
                        summaryTotals.years[year].metrics[lead.yearMonth].totalContacted ++
                        summaryTotals.years[year].metrics[lead.yearMonth].totalBookings ++

                        summaryTotals.years[year].metrics[lead.yearMonth].totalLeadToBookingCreatedAtSMS ++
                        summaryTotals.years[year].metrics[lead.yearMonth].totalLeadToBookingCreatedAtMinsSMS.push(Math.round(DateTime.fromMillis(lead.bookingCreatedAtTimestamp).diff(ts, "minutes").toObject().minutes))
                        
                        if(lead.bookingKept){        
                            summaryTotals.years[year].metrics[lead.yearMonth].totalBookingsKept ++
                        }
                    } else {
                        summaryTotals.years[year].metrics[lead.yearMonth].totalLeadToBookingCreatedAtOther ++
                        summaryTotals.years[year].metrics[lead.yearMonth].totalLeadToBookingCreatedAtMinsOther.push(Math.round(DateTime.fromMillis(lead.bookingCreatedAtTimestamp).diff(ts, "minutes").toObject().minutes))
                    }

                    summaryTotals.years[year].metrics[lead.yearMonth].totalLeadToBookingCreatedAt ++
                    summaryTotals.years[year].metrics[lead.yearMonth].totalLeadToBookingCreatedAtMins.push(Math.round(DateTime.fromMillis(lead.bookingCreatedAtTimestamp).diff(ts, "minutes").toObject().minutes))
                    
                    if(lead.bookingKept){        
                        summaryTotals.years[year].metrics[lead.yearMonth].totalBookingsKeptAllTypes ++
                    }

                    summaryTotals.years[year].metrics[lead.yearMonth].sources[source].totalContactedAllTypes ++
                    summaryTotals.years[year].metrics[lead.yearMonth].personas[persona].totalContactedAllTypes ++
                    summaryTotals.years[year].metrics[lead.yearMonth].personas[persona].totalBookingsAllTypes ++
                    
                    summaryTotals.years[year].metrics[lead.yearMonth].totalContactedAllTypes ++
                    summaryTotals.years[year].metrics[lead.yearMonth].totalBookingsAllTypes ++
                }

                if(lead.rescheduledCounter && 
                    lead.rescheduledTimestamp >= ymStartTs &&
                    lead.rescheduledTimestamp <= ymEndTs){
                    summaryTotals.years[year].metrics[lead.yearMonth].totalBookings += lead.rescheduledCounter
                    summaryTotals.years[year].metrics[lead.yearMonth].totalRescheduled += lead.rescheduledCounter
                } else if(lead.rescheduledTimestamp && 
                    lead.rescheduledTimestamp >= ymStartTs &&
                    lead.rescheduledTimestamp <= ymEndTs){
                    summaryTotals.years[year].metrics[lead.yearMonth].totalBookings ++
                    summaryTotals.years[year].metrics[lead.yearMonth].totalRescheduled ++
                }

                if(lead.signedTimestamp && 
                    lead.signedTimestamp >= ymStartTs &&
                    lead.signedTimestamp <= ymEndTs){
                    summaryTotals.years[year].metrics[lead.yearMonth].totalAppsSigned ++
                }

                if(lead.fddSignedTimestamp && 
                    lead.fddSignedTimestamp >= ymStartTs &&
                    lead.fddSignedTimestamp <= ymEndTs){
                    summaryTotals.years[year].metrics[lead.yearMonth].totalFDDSigned ++
                }

                if(lead.aiInitEngagementTimestamp && !lead.smsIneligible && 
                    lead.aiInitEngagementTimestamp >= ymStartTs &&
                    lead.aiInitEngagementTimestamp <= ymEndTs){
                    summaryTotals.years[year].metrics[lead.yearMonth].totalAIEngaged ++
                }

                if(lead.timestamp >= ymStartTs &&
                    lead.timestamp <= ymEndTs){
                        
                    if(territoryStatus === "Available"){
                        summaryTotals.years[year].metrics[lead.yearMonth].totalOpenTerritories ++
                    } else if(territoryStatus === "Unavailable"){
                        summaryTotals.years[year].metrics[lead.yearMonth].totalClosedTerritories ++
                    } else if(territoryStatus === "Pending"){
                        summaryTotals.years[year].metrics[lead.yearMonth].totalPendingTerritories ++
                    } else if(territoryStatus === "Unknown"){
                        summaryTotals.years[year].metrics[lead.yearMonth].totalUnknownTerritories ++
                    }

                    if(adjustedTerritory){
                        summaryTotals.years[year].metrics[lead.yearMonth].totalAdjustedTerritories ++
                    }


                    if(financiallyQualified){
                        summaryTotals.years[year].metrics[lead.yearMonth].totalFinanciallyQualifiedLeads ++
                    }

                    summaryTotals.years[year].metrics[lead.yearMonth].totalLeads ++
                
                    if(unsubscribed){                        
                        summaryTotals.years[year].metrics[lead.yearMonth].totalUnsubscribed ++
                        summaryTotals.years[year].metrics[lead.yearMonth].personas[persona].totalUnsubscribed ++
                    }
        
                    if(optedOut){
                        summaryTotals.years[year].metrics[lead.yearMonth].totalOptedOut ++
                        summaryTotals.years[year].metrics[lead.yearMonth].personas[persona].totalOptedOut ++
                    }
                }
            }

            // if((lead?.dealType ?? "(N/A)").toLowerCase() !== "broker"){
                if(lead.closeTimestamp && 
                    lead.status.toLowerCase() === "won" && 
                    !dealCloseOverride &&
                    lead.closeTimestamp >= thisMonthStartTs &&
                    lead.closeTimestamp <= thisMonthEndTs){
                    summaryTotals.sourceTypeUnitTotals[key].mediums[medium].revenue += (lead.revenue ?  parseInt(lead.revenue) : 0) - (lead.brokerCommission ?  parseInt(lead.brokerCommission) : 0)
                    summaryTotals.sourceTypeUnitTotals[key].mediums[medium].unitSales += numberOfUnitsSold
                    
                    summaryTotals.years[year].totalWebsiteUnits ++
                }
            // }

            if(summaryTotals.sourceDealTypeUnitTotals[dealTypeKey] === undefined){
                summaryTotals.sourceDealTypeUnitTotals[dealTypeKey] = {
                    key,
                    year: parseInt(year),
                    dealType: lead?.dealType ?? "(N/A)",
                    revenue: 0,
                    unitSales: 0
                }
            }

            if(lead.closeTimestamp && 
                lead.status.toLowerCase() === "won" && 
                !dealCloseOverride &&
                lead.closeTimestamp >= thisMonthStartTs &&
                lead.closeTimestamp <= thisMonthEndTs){
                summaryTotals.sourceTypeUnitTotals[key].revenue += (lead.revenue ? parseInt(lead.revenue) : 0) - (lead.brokerCommission ? parseInt(lead.brokerCommission) : 0)
                summaryTotals.sourceTypeUnitTotals[key].unitSales += numberOfUnitsSold

                summaryTotals.sourceDealTypeUnitTotals[lead.dealTypeKey].revenue += (lead.revenue ? parseInt(lead.revenue) : 0) - (lead.brokerCommission ? parseInt(lead.brokerCommission) : 0)
                summaryTotals.sourceDealTypeUnitTotals[lead.dealTypeKey].unitSales += numberOfUnitsSold
                
            }

            tempFranchiseSales[mKey].metrics[lead.yearMonth].leads.push(lead)

            if(lead.signedTimestamp && 
                lead.signedTimestamp >= thisMonthStartTs &&
                lead.signedTimestamp <= thisMonthEndTs){
                summaryTotals.years[year].totalLeadToAppDays += DateTime.fromMillis(lead.signedTimestamp).diff(ts, "days").toObject().days
                
                summaryTotals.years[year].totalLeadToApp ++
                summaryTotals.years[year].totalAppsSigned ++
                
                summaryTotals.sourceTypeUnitTotals[key].mediums[medium].totalAppsSigned ++    
                summaryTotals.sourceTypeUnitTotals[key].totalAppsSigned ++
                
                summaryTotals.years[year].personas[persona].totalAppsSigned ++            
            }
            
            if(lead.fddSignedTimestamp && 
                lead.fddSignedTimestamp >= thisMonthStartTs &&
                lead.fddSignedTimestamp <= thisMonthEndTs){
                summaryTotals.years[year].totalLeadToAppDays += DateTime.fromMillis(lead.fddSignedTimestamp).diff(ts, "days").toObject().days
                
                summaryTotals.years[year].totalFDDSigned ++
                
                summaryTotals.sourceTypeUnitTotals[key].mediums[medium].totalFDDSigned ++                
            }

            if(lead.closeTimestamp && 
                !dealCloseOverride &&
                lead.status.toLowerCase() === "won" && 
                lead.closeTimestamp >= thisMonthStartTs &&
                lead.closeTimestamp <= thisMonthEndTs){ 
                
                if(lead?.metrics?.minority) {
                    summaryTotals.years[year].totalDEI ++

                    summaryTotals.sourceTypeUnitTotals[key].mediums[medium].totalDEI ++
                }

                summaryTotals.years[year].totalUnits += numberOfUnitsSold

                summaryTotals.years[year].totalLeadToDealDays += DateTime.fromMillis(lead.closeTimestamp).diff(ts, "days").toObject().days
                summaryTotals.years[year].totalLeadToDeal ++
                summaryTotals.years[year].unitSales += numberOfUnitsSold

                summaryTotals.years[year].revenue += (lead.revenue ? parseInt(lead.revenue) : 0) - (lead.brokerCommission ? parseInt(lead.brokerCommission) : 0)
            }
            if((lead.engagementTimestamp && 
                lead.engagementTimestamp >= thisMonthStartTs &&
                lead.engagementTimestamp <= thisMonthEndTs) || (lead.engagedTimestamp &&
                    lead.engagedTimestamp >= thisMonthStartTs &&
                    lead.engagedTimestamp <= thisMonthEndTs)){
                summaryTotals.years[year].totalEngaged ++
                summaryTotals.years[year].personas[persona].totalEngaged ++
                summaryTotals.sourceTypeUnitTotals[key].mediums[medium].totalEngaged ++
            }
            if(lead.bookingCreatedAtTimestamp && 
                lead.bookingCreatedAtTimestamp >= thisMonthStartTs &&
                lead.bookingCreatedAtTimestamp <= thisMonthEndTs){
                
                if(bookingType === "SMS"){
                    if(lead.contactedTimestamp){
                        summaryTotals.years[year].totalLeadToContactedDays += DateTime.fromMillis(lead.contactedTimestamp).diff(ts, "days").toObject().days
                    }
                    summaryTotals.years[year].totalLeadToContacted ++
                    summaryTotals.years[year].totalContacted ++
                    
                    summaryTotals.years[year].totalBookings ++

                    summaryTotals.sourceTypeUnitTotals[key].totalContacted ++
                    summaryTotals.sourceTypeUnitTotals[key].mediums[medium].totalContacted ++

                    summaryTotals.years[year].personas[persona].totalContacted ++
                    summaryTotals.years[year].sources[source].totalContacted ++

                    if(lead.bookingCreatedAtTimestamp){
                        summaryTotals.years[year].totalLeadToBookingCreatedAtMinsSMS.push(Math.round(DateTime.fromMillis(lead.bookingCreatedAtTimestamp).diff(ts, "minutes").toObject().minutes))
                        summaryTotals.years[year].totalLeadToBookingCreatedAtSMS ++
                    }
                    
                    if(lead.bookingKept){        
                        summaryTotals.years[year].totalBookingsKept ++
                        summaryTotals.years[year].personas[persona].totalBookingsKept ++
                        summaryTotals.sourceTypeUnitTotals[key].mediums[medium].totalBookingsKept ++
                    }
                }

                if(lead.bookingCreatedAtTimestamp){
                    summaryTotals.years[year].totalLeadToBookingCreatedAtMins.push(Math.round(DateTime.fromMillis(lead.bookingCreatedAtTimestamp).diff(ts, "minutes").toObject().minutes))
                    summaryTotals.years[year].totalLeadToBookingCreatedAt ++
                }
                
                if(lead.bookingKept){        
                    summaryTotals.years[year].totalBookingsKeptAllTypes ++
                    summaryTotals.years[year].personas[persona].totalBookingsKeptAllTypes ++
                    summaryTotals.sourceTypeUnitTotals[key].mediums[medium].totalBookingsKeptAllTypes ++
                }

                summaryTotals.years[year].totalContactedAllTypes ++
                summaryTotals.years[year].totalBookingsAllTypes ++

                summaryTotals.sourceTypeUnitTotals[key].totalContactedAllTypes ++
                summaryTotals.sourceTypeUnitTotals[key].mediums[medium].totalContactedAllTypes ++

                summaryTotals.years[year].personas[persona].totalContactedAllTypes ++
                summaryTotals.years[year].personas[persona].totalBookingsAllTypes ++
                summaryTotals.years[year].sources[source].totalContactedAllTypes ++
            }

            if(lead.cancelledCounter && 
                lead.cancelledTimestamp >= thisMonthStartTs &&
                lead.cancelledTimestamp <= thisMonthEndTs){
                summaryTotals.years[year].totalCancelled += lead.cancelledCounter
                summaryTotals.years[year].personas[persona].totalCancelled += lead.cancelledCounter
                summaryTotals.sourceTypeUnitTotals[key].mediums[medium].totalCancelled += lead.cancelledCounter

            } else if(lead.cancelledTimestamp && 
                lead.cancelledTimestamp >= thisMonthStartTs &&
                lead.cancelledTimestamp <= thisMonthEndTs){
                summaryTotals.years[year].totalCancelled ++
                summaryTotals.years[year].personas[persona].totalCancelled ++
                summaryTotals.sourceTypeUnitTotals[key].mediums[medium].totalCancelled ++
            }

            if(lead.noShowCounter && 
                lead.noShowTimestamp >= thisMonthStartTs &&
                lead.noShowTimestamp <= thisMonthEndTs){
                summaryTotals.years[year].totalNoShow += lead.noShowCounter
                summaryTotals.years[year].personas[persona].totalNoShow += lead.noShowCounter
                summaryTotals.sourceTypeUnitTotals[key].mediums[medium].totalNoShow += lead.noShowCounter

            } else if(lead.noShowTimestamp && 
                lead.noShowTimestamp >= thisMonthStartTs &&
                lead.noShowTimestamp <= thisMonthEndTs){
                summaryTotals.years[year].totalNoShow ++
                summaryTotals.years[year].personas[persona].totalNoShow ++
                summaryTotals.sourceTypeUnitTotals[key].mediums[medium].totalNoShow ++
            }

            if(lead.rescheduledCounter && 
                lead.rescheduledTimestamp >= thisMonthStartTs &&
                lead.rescheduledTimestamp <= thisMonthEndTs){
                summaryTotals.years[year].totalRescheduled += lead.rescheduledCounter
                summaryTotals.years[year].totalBookings += lead.rescheduledCounter
                summaryTotals.years[year].personas[persona].totalRescheduled += lead.rescheduledCounter
                summaryTotals.sourceTypeUnitTotals[key].mediums[medium].totalRescheduled += lead.rescheduledCounter
                
            } else if(lead.rescheduledTimestamp && 
                lead.rescheduledTimestamp >= thisMonthStartTs &&
                lead.rescheduledTimestamp <= thisMonthEndTs){
                summaryTotals.years[year].totalRescheduled ++
                summaryTotals.years[year].totalBookings ++

                summaryTotals.years[year].personas[persona].totalRescheduled ++
                summaryTotals.sourceTypeUnitTotals[key].mediums[medium].totalRescheduled ++
            }

            if(lead.discoveryDayTimestamp && 
                lead.discoveryDayTimestamp >= thisMonthStartTs &&
                lead.discoveryDayTimestamp <= thisMonthEndTs){
                summaryTotals.years[year].totalDiscovered ++
                summaryTotals.years[year].personas[persona].totalDiscovered ++

                summaryTotals.sourceTypeUnitTotals[key].mediums[medium].totalDiscovered ++
                summaryTotals.sourceTypeUnitTotals[key].totalDiscovered ++
            }

            if(lead.aiInitEngagementTimestamp && !lead.smsIneligible && 
                lead.aiInitEngagementTimestamp >= thisMonthStartTs &&
                lead.aiInitEngagementTimestamp <= thisMonthEndTs){
                summaryTotals.years[year].totalAIEngaged ++
                summaryTotals.years[year].personas[persona].totalAIEngaged ++
            }

            if(lead.timestamp >= thisMonthStartTs &&
                lead.timestamp <= thisMonthEndTs){
                summaryTotals.years[year].totalLeads ++        
                summaryTotals.years[year].personas[persona].totalLeads ++    

                summaryTotals.sourceTypeUnitTotals[key].totalLeads ++
                summaryTotals.sourceTypeUnitTotals[key].mediums[medium].totalLeads ++

                if(dqReason){
                    summaryTotals.years[year].totalDqReasonLeads ++

                    summaryTotals.years[year].dqReasons[dqReason].totalLeads ++
                    summaryTotals.years[year].dqReasons[dqReason].sources[source].totalLeads ++
                    summaryTotals.years[year].dqReasons[dqReason].sources[source].mediums[medium].totalLeads ++
                    
                    summaryTotals.years[year].dqReasons[dqReason].dealValue += lead?.revenue ?? 0
                    summaryTotals.years[year].dqReasons[dqReason].sources[source].dealValue += lead?.revenue ?? 0
                    summaryTotals.years[year].dqReasons[dqReason].sources[source].mediums[medium].dealValue += lead?.revenue ?? 0

                    summaryTotals.sourceTypeUnitTotals[key].mediums[medium].totalDqReasonLeads ++
                }

                if(financiallyQualified){
                    summaryTotals.years[year].totalFinanciallyQualifiedLeads ++
                    
                    summaryTotals.sourceTypeUnitTotals[key].mediums[medium].totalFinanciallyQualifiedLeads ++
                    summaryTotals.sourceTypeUnitTotals[key].totalFinanciallyQualifiedLeads ++
                }
                
                if(unsubscribed){
                    summaryTotals.years[year].totalUnsubscribed ++
    
                    summaryTotals.sourceTypeUnitTotals[key].mediums[medium].totalUnsubscribed ++
                }
    
                if(optedOut){
                    summaryTotals.years[year].totalOptedOut ++
    
                    summaryTotals.sourceTypeUnitTotals[key].mediums[medium].totalOptedOut ++
                }
            }
        })
    }
    if(summaryTotals.years[currentYear] === undefined) {
        summaryTotals.years[currentYear] = {
            metrics: {},
            sources: {},
            personas: {},
            revenue: 0,
            totalLeadToApp: 0,
            totalLeadToAppDays: 0,
            totalLeadToContacted: 0,
            totalLeadToContactedDays: 0,
            totalLeadToDeal: 0,
            totalLeadToDealDays: 0,
            totalDEI: 0,
            totalUnits: 0,
            totalWebsiteUnits: 0,
            totalEngaged: 0,
            totalContacted: 0,
            totalContactedAllTypes: 0,
            totalBookings: 0,
            totalBookingsAllTypes: 0,
            totalAppsSigned: 0,
            totalFDDSigned: 0,
            totalLeads: 0,
            unitSales: 0,
            totalRescheduled: 0,
            totalNoShow: 0,
            totalCancelled: 0,
            totalDiscovered: 0,
            totalBookingsKept: 0,
            totalBookingsKeptAllTypes: 0,
            totalFinanciallyQualifiedLeads: 0,
            totalOpenTerritories: 0,
            totalPendingTerritories: 0,
            totalUnknownTerritories: 0,
            totalAdjustedTerritories: 0,
            totalClosedTerritories: 0,
            totalAIEngaged: 0,
            totalLeadToBookingCreatedAt: 0,
            totalLeadToBookingCreatedAtMins: [],
            totalLeadToBookingCreatedAtSMS: 0,
            totalLeadToBookingCreatedAtMinsSMS: [],
            totalLeadToBookingCreatedAtOther: 0,
            totalLeadToBookingCreatedAtMinsOther: []
        }

        if(summaryTotals.years[currentYear-1] === undefined) {
            summaryTotals.years[currentYear-1] = {
                metrics: {},
                sources: {},
                personas: {},
                revenue: 0,
                totalLeadToApp: 0,
                totalLeadToAppDays: 0,
                totalLeadToContacted: 0,
                totalLeadToContactedDays: 0,
                totalLeadToDeal: 0,
                totalLeadToDealDays: 0,
                totalDEI: 0,
                totalUnits: 0,
                totalWebsiteUnits: 0,
                totalEngaged: 0,
                totalContacted: 0,
                totalContactedAllTypes: 0,
                totalBookings: 0,
                totalBookingsAllTypes: 0,
                totalAppsSigned: 0,
                totalFDDSigned: 0,
                totalLeads: 0,
                unitSales: 0,
                totalRescheduled: 0,
                totalNoShow: 0,
                totalCancelled: 0,
                totalDiscovered: 0,
                totalBookingsKept: 0,
                totalBookingsKeptAllTypes: 0,
                totalFinanciallyQualifiedLeads: 0,
                totalOpenTerritories: 0,
                totalClosedTerritories: 0,
                totalPendingTerritories: 0,
                totalUnknownTerritories: 0,
                totalAdjustedTerritories: 0,
                totalAIEngaged: 0,
                totalLeadToBookingCreatedAt: 0,
                totalLeadToBookingCreatedAtMins: [],
                totalLeadToBookingCreatedAtSMS: 0,
                totalLeadToBookingCreatedAtMinsSMS: [],
                totalLeadToBookingCreatedAtOther: 0,
                totalLeadToBookingCreatedAtMinsOther: []
            }
        }

        for(let i = 1; i <= 12; i ++){
            let yMM = parseInt(currentYear + (i > 9 ? "" + i: "0" + i));
            let lyMM = parseInt((currentYear-1) + (i > 9 ? "" + i: "0" + i));
            summaryTotals.years[currentYear-1].metrics[lyMM] = {
                unitSales: 0, revenue: 0, totalLeads: 0,
                totalContacted: 0, totalContactedAllTypes: 0,
                totalBookings: 0, totalBookingsAllTypes: 0, 
                sources: {}, personas: {}, 
                totalBookingsKept: 0, totalBookingsKeptAllTypes: 0,
                totalAppsSigned: 0, totalFDDSigned: 0, totalRescheduled: 0,
                totalNoShow: 0, totalCancelled: 0, totalFinanciallyQualifiedLeads: 0,
                totalDiscovered: 0, totalOpenTerritories: 0, totalClosedTerritories: 0,
                totalPendingTerritories: 0,
                totalUnknownTerritories: 0,
                totalAdjustedTerritories: 0,
                totalLeadToBookingCreatedAt: 0,
                totalLeadToBookingCreatedAtMins: []
            }
            
            // if(lastEnabledMonth >= yMM){
                summaryTotals.years[currentYear].metrics[yMM] = {
                    unitSales: 0, revenue: 0, totalLeads: 0,
                    totalContacted: 0, totalContactedAllTypes: 0, 
                    totalBookings: 0, totalBookingsAllTypes: 0, 
                    sources: {}, personas: {}, 
                    totalBookingsKept: 0, totalBookingsKeptAllTypes: 0,
                    totalAppsSigned: 0, totalFDDSigned: 0, totalRescheduled: 0,
                    totalNoShow: 0, totalCancelled: 0, totalFinanciallyQualifiedLeads: 0,
                    totalDiscovered: 0, totalOpenTerritories: 0, totalClosedTerritories: 0,
                    totalPendingTerritories: 0,
                    totalUnknownTerritories: 0,
                    totalAdjustedTerritories: 0,
                    totalLeadToBookingCreatedAt: 0,
                    totalLeadToBookingCreatedAtMins: []
                }
            // }
        }
    }

    Object.keys(summaryTotals.years).forEach(year => {
        Object.keys(summaryTotals.years[year].metrics).forEach(yM => {
            let item = summaryTotals.years[year].metrics[yM]
            Object.keys(summaryTotals.years[year].metrics[yM].sources).forEach(source => {
                let key = year + source
                
                if(summaryTotals.sourceTypeUnitTotals[key] === undefined){
                    summaryTotals.sourceTypeUnitTotals[key] = {
                        key,
                        year: parseInt(year),
                        source: source ?? "(N/A)",
                        revenue: 0,
                        unitSales: 0,
                        totalLeads: 0,
                        totalFinanciallyQualifiedLeads: 0,
                        totalAppsSigned: 0,
                        totalDiscovered: 0,
                        totalContacted: 0,
                        totalContactedAllTypes: 0,
                        mediums: {}
                    }
                }
                if(summaryTotals.years[year].metrics[yM].sources[source].mediums){
                    Object.keys(summaryTotals.years[year].metrics[yM].sources[source].mediums).forEach(medium => {
                        let leadStats = summaryTotals.years[year].metrics[yM].sources[source].mediums
                        if(summaryTotals.sourceTypeUnitTotals[key].mediums[medium] === undefined){
                            summaryTotals.sourceTypeUnitTotals[key].mediums[medium] = {
                                revenue: 0,
                                unitSales: 0,
                                totalLeads: 0,
                                totalEngaged: 0,
                                totalContacted: 0,
                                totalContactedAllTypes: 0,
                                totalBookings: 0,
                                totalBookingsAllTypes: 0,
                                totalAppsSigned: 0,
                                totalFDDSigned: 0,
                                totalCancelled: 0,
                                totalNoShow: 0,
                                totalRescheduled: 0,
                                totalDiscovered: 0,
                                totalBookingsKept: 0,
                                totalBookingsKeptAllTypes: 0,
                                totalOpenTerritories: 0,
                                totalPendingTerritories: 0,
                                totalUnknownTerritories: 0,
                                totalAdjustedTerritories: 0,
                                totalClosedTerritories: 0,
                                totalFinanciallyQualifiedLeads: 0
                            }
                        }
            
                        // summaryTotals.sourceTypeUnitTotals[key].mediums[medium].revenue += parseInt(leadStats.revenue)
                        // summaryTotals.sourceTypeUnitTotals[key].mediums[medium].unitSales += leadStats.unitSales

                    })
                }

                // summaryTotals.sourceTypeUnitTotals[key].revenue += parseInt(item.revenue)
                // summaryTotals.sourceTypeUnitTotals[key].unitSales += item.unitSales
            })

            // tempFranchiseSales[key].metrics[lead.yearMonth].leads.push(lead)
        })
        if(summaryTotals.years[year].dealTypes){
            Object.keys(summaryTotals.years[year].dealTypes).forEach(dealType => {
                let dealTypeKey = year + dealType
                let dealTypeItem = summaryTotals.years[year].dealTypes[dealType]
                if(summaryTotals.sourceDealTypeUnitTotals[dealTypeKey] === undefined){
                    summaryTotals.sourceDealTypeUnitTotals[dealTypeKey] = {
                        year: parseInt(year),
                        dealType: dealType ?? "(N/A)",
                        revenue: 0,
                        unitSales: 0
                    }
                }

                // summaryTotals.sourceDealTypeUnitTotals[dealTypeKey].revenue += parseInt(dealTypeItem.revenue)
                // summaryTotals.sourceDealTypeUnitTotals[dealTypeKey].unitSales += dealTypeItem.unitSales

                // if((dealType ?? "(N/A)").toLowerCase() !== "broker"){
                //     summaryTotals.years[year].totalWebsiteUnits += dealTypeItem.unitSales
                // }
            })
        }
    })

    summaryTotals.sourceTypeUnitTotalsObj = summaryTotals.sourceTypeUnitTotals
    summaryTotals.sourceTypeUnitTotals = Object.values(summaryTotals.sourceTypeUnitTotals)
    summaryTotals.sourceDealTypeUnitTotals = Object.values(summaryTotals.sourceDealTypeUnitTotals)

    return {
        franchiseSales: Object.values(tempFranchiseSales),
        summaryTotals
    }
}

export const prependFunnelOptions = () => {
    
}